import JSZip from 'jszip';
import jsquashResize from '@jsquash/resize';
import { encode as encodeJpeg } from '@jsquash/jpeg';
import { encode as encodeWebp } from '@jsquash/webp';
import { encode as encodeAvif } from '@jsquash/avif';
import initOxipng, { optimise_raw as optimiseOxiPngRaw } from '@jsquash/oxipng/codec/pkg/squoosh_oxipng.js';
import photonInit, * as photon from '@silvia-odwyer/photon';
import photonWasmUrl from '@silvia-odwyer/photon/photon_rs_bg.wasm';
import DEFAULT_PROFILES from './defaultProfiles.js';
import ASPECT_PRESETS from './aspectPresets.js';
import profileDefault from './profileDefault.js';
import { aspectRatioExact, classifyAspect, simplifyRatioString } from './aspectRatioUtils.js';

let images = [];
let gridItemSize = 240;
let selectedImageId = null;
let currentAspectRatio = 16/9;
let isResizing = false;
let startX = 0;
let startWidth = 0;
const STORAGE_KEY = 'batchImageProcessorPrefs';
const DEFAULT_JSQUASH_METHOD = 'lanczos3';
const DEFAULT_PHOTON_SAMPLING_FILTER = 5;
const DEFAULT_SCALE_PERCENT = 100;
const RESIZE_MODES = {
    FIXED_ASPECT: 'fixed_aspect',
    FIXED_ASPECT_CROP: 'fixed_aspect_crop',
    STATIC_SIZE: 'static_size',
    FIXED_WIDTH: 'fixed_width',
    FIXED_HEIGHT: 'fixed_height',
    SCALE_TO_FIT: 'scale_to_fit',
    FREE_CROP: 'free_crop',
    NO_CROP: 'no_crop',
    SCALED: 'scaled'
};
const DEFAULT_RESIZE_MODE = RESIZE_MODES.FIXED_ASPECT;

const OXIPNG_DEFAULTS = {
    level: 2,
    interlace: false,
    optimiseAlpha: false
};

let oxipngReady = null;
let exportPreviewDebounceTimer = null;
let isSplitPreview = false;
let exportPreviewRequestId = 0;
let isExportPreviewVisible = false;
let exportPreviewLastScheduledChangeId = 0;
let exportPreviewLastRenderedChangeId = 0;
let cropChangeId = 0;
let exportSettingsChangeId = 0;
let lastExportPreview = {
    imageId: null,
    cropChangeId: 0,
    settingsChangeId: 0,
    blobUrl: null,
    sizeBytes: null,
    width: null,
    height: null
};
let photonReady = null;
let profilesVisible = false;
let currentProfileName = '';
let profileModified = false;
let suppressProfileModified = false;
let lastLoadedProfile = null;
let lastExportPreviewMode = 'none';
let lastSplitPosition = null;
let lastSplitPositionVertical = null;
let lastSplitPositionFullscreen = null;
let lastSplitPositionVerticalFullscreen = null;
let splitOrientation = 'horizontal';
let lastResizeMode = DEFAULT_RESIZE_MODE;
let exportInProgress = false;
let exportCancelRequested = false;
let isDirty = false;
let suppressDirty = true;
let forcedFullscreenPreview = false;
let previewFullscreen = false;
let fullscreenSplitNeedsReset = false;
let pendingClosePreviewAfterFullscreen = null;
let fallbackWarningActive = false;
const fallbackWarningState = {
    resize: false,
    encode: false
};
let showGridSizeInfo = false;
let showFilenameTip = true;
let resumeGridRefreshTimer = null;
let nudgeBoostEnabled = false;
const HEIC_TO_IIFE_URLS = [
    'https://cdn.jsdelivr.net/npm/heic-to@1.4.2/dist/iife/heic-to.js',
    'https://cdn.jsdelivr.net/npm/heic-to@1.3.0/dist/iife/heic-to.js'
];
const IMPORT_PROGRESS_DELAY_MS = 1000;
let heicToLoadPromise = null;
let nativeHeicSupportChecked = false;
let nativeHeicSupported = false;
let activeImportProgressId = 0;

function getForcedFallbackMode() {
    const params = new URLSearchParams(window.location.search);
    const param = (params.get('forceFallback') || '').toLowerCase().trim();
    let stored = '';
    try {
        stored = (localStorage.getItem('forceFallback') || '').toLowerCase().trim();
    } catch (err) {
        stored = '';
    }
    const mode = param || stored;
    if (!mode) return null;
    if (mode === 'resize' || mode === 'encode') return mode;
    if (mode === 'both' || mode === 'all' || mode === 'true' || mode === '1') return 'both';
    return null;
}

function getPreviewDelayMs(forSettings = false) {
    const select = document.getElementById('previewDelay');
    if (!select) return 500;
    const value = select.value;
    if (value === 'off') {
        return forSettings ? 500 : null;
    }
    const seconds = parseFloat(value);
    if (!Number.isFinite(seconds)) {
        return forSettings ? 500 : null;
    }
    return Math.round(seconds * 1000);
}

function getPreviewDelayMsDrag() {
    return getPreviewDelayMs(false);
}

function getPreviewDelayMsMouseup() {
    const select = document.getElementById('previewDelay');
    if (!select) return 180;
    const value = select.value;
    if (value === 'off') {
        return 180;
    }
    const seconds = parseFloat(value);
    if (!Number.isFinite(seconds)) {
        return 180;
    }
    return Math.round(seconds * 1000);
}

function updateTestExportButton() {
    const testExportBtn = document.getElementById('testExportBtn');
    if (!testExportBtn) return;
    if (isSplitPreview) {
        testExportBtn.style.display = 'none';
        return;
    }
    testExportBtn.style.display = 'inline-flex';
    testExportBtn.textContent = isExportPreviewVisible ? 'Hide Export' : 'Show Export';
}

async function getPhoton() {
    if (!photonReady) {
        photonReady = photonInit({ module_or_path: photonWasmUrl });
    }
    await photonReady;
    return photon;
}

function setFallbackWarning(type) {
    if (type === 'resize') {
        fallbackWarningState.resize = true;
    } else if (type === 'encode') {
        fallbackWarningState.encode = true;
    }

    if (!fallbackWarningActive) {
        fallbackWarningActive = true;
        document.body.classList.add('has-fallback-warning');
    }

    const detailEl = document.getElementById('fallbackWarningDetail');
    if (!detailEl) return;
    const parts = [];
    if (fallbackWarningState.resize) parts.push('resize');
    if (fallbackWarningState.encode) parts.push('encode');
    const label = parts.length === 2 ? 'resize/encode' : (parts[0] || 'processing');
    detailEl.textContent = `High-quality ${label} failed, using canvas fallback (reduced quality).`;
}

function blendSharpenedImage(original, sharpened, strength, threshold) {
    const src = original.data;
    const sharp = sharpened.data || sharpened;
    const out = new Uint8ClampedArray(src.length);
    const thresh = Math.max(0, Math.min(255, threshold || 0));
    const s = Math.max(0, Math.min(2, strength || 0));

    for (let i = 0; i < src.length; i += 4) {
        for (let c = 0; c < 3; c += 1) {
            const o = src[i + c];
            const sh = sharp[i + c];
            const diff = Math.abs(sh - o);
            if (diff < thresh) {
                out[i + c] = o;
            } else {
                const v = o + (sh - o) * s;
                out[i + c] = Math.max(0, Math.min(255, Math.round(v)));
            }
        }
        out[i + 3] = src[i + 3];
    }

    return new ImageData(out, original.width, original.height);
}

async function applySharpening(imageData) {
    const enableSharpening = document.getElementById('enableSharpening').checked;
    if (!enableSharpening) return imageData;

    const amount = parseInt(document.getElementById('sharpenAmount').value, 10) || 0;
    const radius = parseFloat(document.getElementById('sharpenRadius').value) || 1;
    const threshold = parseInt(document.getElementById('sharpenThreshold').value, 10) || 0;
    const adaptive = document.getElementById('adaptiveSharpening').checked;

    let strength = amount / 100;
    const radiusScale = Math.max(0.3, Math.min(2.5, radius));
    strength *= radiusScale;
    if (adaptive) {
        const size = Math.min(imageData.width, imageData.height);
        const sizeScale = Math.max(0.6, Math.min(1.5, size / 1000));
        strength *= sizeScale;
    }
    strength = Math.max(0, Math.min(2, strength));
    if (strength === 0) {
        console.log('Sharpening skipped (strength 0)');
        return imageData;
    }

    try {
        console.log('Sharpening enabled', { amount, radius, threshold, adaptive, strength });
        const photonApi = await getPhoton();
        const canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        const ctx = canvas.getContext('2d', { alpha: true });
        ctx.putImageData(imageData, 0, 0);
        if (!photonApi || typeof photonApi.open_image !== 'function') {
            throw new Error('Photon wasm not loaded');
        }
        const photonImage = photonApi.open_image(canvas, ctx);
        if (photonApi.conv && typeof photonApi.conv.sharpen === 'function') {
            photonApi.conv.sharpen(photonImage);
        } else if (typeof photonApi.sharpen === 'function') {
            photonApi.sharpen(photonImage);
        }
        const sharpened = photonApi.to_image_data(photonImage);
        console.log('Sharpening applied');
        return blendSharpenedImage(imageData, sharpened, strength, threshold);
    } catch (err) {
        console.warn('Sharpening failed, returning original image data.', err);
        return imageData;
    }
}

function getResizeMode() {
    return document.getElementById('resizeMode')?.value || DEFAULT_RESIZE_MODE;
}

function clampScalePercent(value) {
    if (!Number.isFinite(value)) return DEFAULT_SCALE_PERCENT;
    return Math.max(1, Math.min(400, Math.round(value)));
}

function getScalePercent() {
    const scaleInput = document.getElementById('scalePercent');
    if (!scaleInput) return DEFAULT_SCALE_PERCENT;
    const value = parseInt(scaleInput.value, 10);
    return clampScalePercent(value);
}

function updateScalePresetButtons() {
    const scale = getScalePercent();
    document.querySelectorAll('.scale-preset-btn').forEach((btn) => {
        const value = parseInt(btn.dataset.scale, 10);
        btn.classList.toggle('active', Number.isFinite(value) && value === scale);
    });
}

function handleScalePercentInput(shouldPersist = true) {
    const scaleInput = document.getElementById('scalePercent');
    if (!scaleInput) return;
    const clamped = getScalePercent();
    scaleInput.value = clamped;
    updateScalePresetButtons();
    if (!shouldPersist) return;
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

function setScalePercentPreset(percent) {
    const scaleInput = document.getElementById('scalePercent');
    if (!scaleInput) return;
    scaleInput.value = clampScalePercent(percent);
    handleScalePercentInput(true);
}

function isAspectLockedMode(mode = getResizeMode()) {
    return mode === RESIZE_MODES.FIXED_ASPECT
        || mode === RESIZE_MODES.FIXED_ASPECT_CROP
        || mode === RESIZE_MODES.STATIC_SIZE;
}

function shouldShowCropOverlay(mode = getResizeMode()) {
    return mode !== RESIZE_MODES.NO_CROP && mode !== RESIZE_MODES.SCALED;
}

function createProcessingSnapshot(img) {
    return {
        settingsId: exportSettingsChangeId,
        cropId: img?._lastCropChangeId || 0
    };
}

function hasProcessingChanged(snapshot, img) {
    if (!snapshot) return false;
    if (exportSettingsChangeId !== snapshot.settingsId) return true;
    const cropId = img?._lastCropChangeId || 0;
    return cropId !== snapshot.cropId;
}

function assertNotInterrupted(snapshot, img) {
    if (hasProcessingChanged(snapshot, img)) {
        const err = new Error('Interrupted');
        err.code = 'INTERRUPTED';
        throw err;
    }
}

function assertNotCancelled(shouldCancel) {
    if (shouldCancel && shouldCancel()) {
        const err = new Error('Cancelled');
        err.code = 'CANCELLED';
        throw err;
    }
}

async function yieldToUI(delayMs = 1) {
    await new Promise(resolve => setTimeout(resolve, delayMs));
}

function getResizeEngineSettings() {
    const engine = document.getElementById('resizeEngine')?.value || 'jsquash';
    const usePhotonResize = engine === 'photon';
    const jsquashMethod = document.getElementById('jsquashResizeMethod')?.value || DEFAULT_JSQUASH_METHOD;
    const photonRaw = parseInt(document.getElementById('photonResizeFilter')?.value, 10);
    const photonSamplingFilter = Number.isFinite(photonRaw)
        ? Math.min(5, Math.max(1, photonRaw))
        : DEFAULT_PHOTON_SAMPLING_FILTER;
    return { usePhotonResize, jsquashMethod, photonSamplingFilter, engine };
}

function syncResizeEngineControls() {
    const engineSelect = document.getElementById('resizeEngine');
    const jsquashSelect = document.getElementById('jsquashResizeMethod');
    const photonSelect = document.getElementById('photonResizeFilter');
    const jsquashGroup = document.getElementById('jsquashFilterGroup');
    const photonGroup = document.getElementById('photonFilterGroup');
    if (!engineSelect || !jsquashSelect || !photonSelect) return;
    const usingPhoton = engineSelect.value === 'photon';
    if (jsquashGroup) {
        jsquashGroup.style.display = usingPhoton ? 'none' : 'block';
    }
    if (photonGroup) {
        photonGroup.style.display = usingPhoton ? 'block' : 'none';
    }
    jsquashSelect.disabled = usingPhoton;
    photonSelect.disabled = !usingPhoton;
}

async function resizeWithPhoton(imageData, width, height, samplingFilter) {
    const photonApi = await getPhoton();
    if (!photonApi || typeof photonApi.open_image !== 'function') {
        throw new Error('Photon wasm not loaded');
    }
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.putImageData(imageData, 0, 0);

    const photonImage = photonApi.open_image(canvas, ctx);
    const filter = Number.isFinite(samplingFilter) ? samplingFilter : DEFAULT_PHOTON_SAMPLING_FILTER;
    if (typeof photonApi.resize === 'function') {
        const resizedPhotonImage = photonApi.resize(photonImage, width, height, filter);
        return photonApi.to_image_data(resizedPhotonImage);
    }
    if (typeof photonApi.resize_img_browser === 'function') {
        const resizedCanvas = photonApi.resize_img_browser(photonImage, width, height, filter);
        const resizedCtx = resizedCanvas.getContext('2d', { alpha: true });
        return resizedCtx.getImageData(0, 0, width, height);
    }
    throw new Error('Photon resize API not available');
}

function getExportOutputDimensions(img, mode = getResizeMode()) {
    if (!img) {
        return { width: 0, height: 0, shouldResize: false, crop: null };
    }
    if (mode === RESIZE_MODES.NO_CROP) {
        return {
            width: img.width,
            height: img.height,
            shouldResize: false,
            crop: { x: 0, y: 0, width: img.width, height: img.height }
        };
    }
    if (mode === RESIZE_MODES.SCALED) {
        const scale = getScalePercent();
        const width = Math.max(1, Math.round((img.width * scale) / 100));
        const height = Math.max(1, Math.round((img.height * scale) / 100));
        return {
            width,
            height,
            shouldResize: width !== img.width || height !== img.height,
            crop: { x: 0, y: 0, width: img.width, height: img.height }
        };
    }

    const crop = img.crop || { x: 0, y: 0, width: img.width, height: img.height };
    const cropWidth = Math.max(1, Math.round(crop.width));
    const cropHeight = Math.max(1, Math.round(crop.height));
    const cropAspect = cropWidth / cropHeight;

    if (mode === RESIZE_MODES.FREE_CROP || mode === RESIZE_MODES.FIXED_ASPECT_CROP) {
        return {
            width: cropWidth,
            height: cropHeight,
            shouldResize: false,
            crop
        };
    }

    if (mode === RESIZE_MODES.FIXED_WIDTH) {
        const fixedWidth = parseInt(document.getElementById('exportWidth').value);
        const width = Number.isFinite(fixedWidth) && fixedWidth > 0 ? fixedWidth : cropWidth;
        const height = Math.max(1, Math.round(width / cropAspect));
        return { width, height, shouldResize: true, crop };
    }

    if (mode === RESIZE_MODES.FIXED_HEIGHT) {
        const fixedHeight = parseInt(document.getElementById('exportHeight').value);
        const height = Number.isFinite(fixedHeight) && fixedHeight > 0 ? fixedHeight : cropHeight;
        const width = Math.max(1, Math.round(height * cropAspect));
        return { width, height, shouldResize: true, crop };
    }

    if (mode === RESIZE_MODES.SCALE_TO_FIT) {
        const maxWidth = parseInt(document.getElementById('exportWidth').value);
        const maxHeight = parseInt(document.getElementById('exportHeight').value);
        const widthLimit = Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : cropWidth;
        const heightLimit = Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : cropHeight;
        const scale = Math.min(widthLimit / cropWidth, heightLimit / cropHeight);
        const width = Math.max(1, Math.round(cropWidth * scale));
        const height = Math.max(1, Math.round(cropHeight * scale));
        return {
            width,
            height,
            shouldResize: width !== cropWidth || height !== cropHeight,
            crop
        };
    }

    const exportWidth = parseInt(document.getElementById('exportWidth').value);
    const exportHeight = parseInt(document.getElementById('exportHeight').value);
    const width = Number.isFinite(exportWidth) && exportWidth > 0 ? exportWidth : cropWidth;
    const height = Number.isFinite(exportHeight) && exportHeight > 0 ? exportHeight : cropHeight;
    return { width, height, shouldResize: true, crop };
}

function setInputEnabled(input, enabled, fallbackValue = null) {
    if (!input) return;
    if (enabled) {
        input.disabled = false;
        if (input.value === '') {
            if (input.dataset.stashedValue) {
                input.value = input.dataset.stashedValue;
            } else if (fallbackValue !== null) {
                input.value = fallbackValue;
            }
        }
        return;
    }
    if (input.value !== '') {
        input.dataset.stashedValue = input.value;
    }
    input.value = '';
    input.disabled = true;
}

function setCropOverlayVisible(visible) {
    if (!visible) {
        const grid = document.getElementById('imagesGrid');
        if (grid) {
            grid.querySelectorAll('.crop-box').forEach(box => {
                if (box._resizeObserver && typeof box._resizeObserver.disconnect === 'function') {
                    box._resizeObserver.disconnect();
                }
                box.remove();
            });
        }
        const previewWrapper = document.getElementById('previewWrapper');
        if (previewWrapper) {
            previewWrapper.querySelectorAll('.crop-box').forEach(box => {
                if (box._resizeObserver && typeof box._resizeObserver.disconnect === 'function') {
                    box._resizeObserver.disconnect();
                }
                box.remove();
            });
        }
        return;
    }
    updateAllCropBoxes();
}

function updateResizeModeUI() {
    const mode = getResizeMode();
    const aspectSection = document.getElementById('aspectRatioSection');
    const exportSizeSection = document.getElementById('exportSizeSection');
    const scaleSection = document.getElementById('scaleSection');
    const exportWidth = document.getElementById('exportWidth');
    const exportHeight = document.getElementById('exportHeight');
    const defaultWidth = Number.isFinite(profileDefault.exportWidth) ? profileDefault.exportWidth : 1920;
    const defaultHeight = Number.isFinite(profileDefault.exportHeight) ? profileDefault.exportHeight : 1080;

    if (aspectSection) {
        aspectSection.classList.toggle(
            'collapsed',
            mode !== RESIZE_MODES.FIXED_ASPECT && mode !== RESIZE_MODES.FIXED_ASPECT_CROP
        );
    }
    if (exportSizeSection) {
        exportSizeSection.classList.toggle(
            'collapsed',
            mode === RESIZE_MODES.NO_CROP
                || mode === RESIZE_MODES.SCALED
                || mode === RESIZE_MODES.FIXED_ASPECT_CROP
                || mode === RESIZE_MODES.FREE_CROP
        );
    }
    if (scaleSection) {
        scaleSection.classList.toggle('collapsed', mode !== RESIZE_MODES.SCALED);
    }

    if (mode === RESIZE_MODES.FIXED_ASPECT
        || mode === RESIZE_MODES.FIXED_ASPECT_CROP
        || mode === RESIZE_MODES.STATIC_SIZE
        || mode === RESIZE_MODES.SCALE_TO_FIT) {
        setInputEnabled(exportWidth, true, defaultWidth);
        setInputEnabled(exportHeight, true, defaultHeight);
    } else if (mode === RESIZE_MODES.FIXED_WIDTH) {
        setInputEnabled(exportWidth, true, defaultWidth);
        setInputEnabled(exportHeight, false, defaultHeight);
    } else if (mode === RESIZE_MODES.FIXED_HEIGHT) {
        setInputEnabled(exportWidth, false, defaultWidth);
        setInputEnabled(exportHeight, true, defaultHeight);
    } else {
        setInputEnabled(exportWidth, false, defaultWidth);
        setInputEnabled(exportHeight, false, defaultHeight);
    }

    if (mode === RESIZE_MODES.STATIC_SIZE) {
        const width = parseInt(exportWidth.value);
        const height = parseInt(exportHeight.value);
        if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
            currentAspectRatio = width / height;
            updateAspectInputsFromCurrent();
        }
    } else if (mode === RESIZE_MODES.FIXED_ASPECT) {
        updateAspectInputsFromCurrent();
        const width = parseInt(exportWidth.value, 10);
        const height = parseInt(exportHeight.value, 10);
        if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
            currentAspectRatio = width / height;
        }
    } else if (mode === RESIZE_MODES.FIXED_ASPECT_CROP) {
        const aspectW = parseFloat(document.getElementById('aspectW')?.value);
        const aspectH = parseFloat(document.getElementById('aspectH')?.value);
        if (Number.isFinite(aspectW) && Number.isFinite(aspectH) && aspectH > 0) {
            currentAspectRatio = aspectW / aspectH;
            const width = parseInt(exportWidth.value, 10);
            if (Number.isFinite(width) && width > 0) {
                exportHeight.value = Math.round(width / currentAspectRatio);
            }
        }
    } else if (mode === RESIZE_MODES.SCALED) {
        handleScalePercentInput(false);
    }

    if (isAspectLockedMode(mode) && mode !== lastResizeMode) {
        images.forEach(img => {
            img.crop = calculateInitialCrop(img.width, img.height);
        });
    }

    setCropOverlayVisible(shouldShowCropOverlay(mode));
    lastResizeMode = mode;
    updateAspectMismatchUI();
}

// Initialize
document.getElementById('fileInput').addEventListener('change', handleFiles);
document.getElementById('aboutModal').addEventListener('click', (e) => {
    if (e.target.id === 'aboutModal') {
        closeAbout();
    }
});
document.getElementById('changelogModal').addEventListener('click', (e) => {
    if (e.target.id === 'changelogModal') {
        closeChangelog();
    }
});
window.addEventListener('beforeunload', (e) => {
    if (!isDirty) return;
    e.preventDefault();
    e.returnValue = 'Do you wish to save changes?';
});
document.addEventListener('keydown', (e) => {
    const target = e.target;
    const targetTag = target && target.tagName ? target.tagName.toLowerCase() : '';
    const isTypingTarget = Boolean(
        target?.isContentEditable
        || targetTag === 'input'
        || targetTag === 'textarea'
        || targetTag === 'select'
        || targetTag === 'button'
    );

    if ((e.code === 'Space' || e.key === ' ') && !e.repeat && !isTypingTarget) {
        if (!selectedImageId) return;
        const modal = document.getElementById('aboutModal');
        if (modal?.classList.contains('active')) return;
        e.preventDefault();
        const previewPane = document.getElementById('previewPane');
        if (!previewPane?.classList.contains('active')) {
            showPreview(selectedImageId);
        }
        togglePreviewFullscreen();
        return;
    }

    if (e.key !== 'Escape') return;
    if (window.innerWidth < PREVIEW_FULLSCREEN_BREAKPOINT) {
        const previewPaneEl = document.getElementById('previewPane');
        const isNativeFullscreen = previewPaneEl && document.fullscreenElement === previewPaneEl;
        if (isNativeFullscreen) {
            document.exitFullscreen();
            return;
        }
        if (previewFullscreen && previewPaneEl?.classList.contains('active')) {
            togglePreviewFullscreen();
            return;
        }
    }
    const changelogModal = document.getElementById('changelogModal');
    if (changelogModal?.classList.contains('active')) {
        closeChangelog();
        return;
    }
    const previewPane = document.getElementById('previewPane');
    const exportOverlay = document.querySelector('.export-preview-overlay');
    if (exportOverlay && !isSplitPreview) {
        closeExportPreview();
        return;
    }
    if (previewPane && previewPane.classList.contains('active')) {
        closePreview();
        return;
    }
    const modal = document.getElementById('aboutModal');
    if (modal.classList.contains('active')) {
        closeAbout();
    }
});

document.addEventListener('paste', (e) => {
    if (!e.clipboardData || !e.clipboardData.items) return;
    const imagesFromClipboard = [];
    for (const item of e.clipboardData.items) {
        if (item.type && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
                imagesFromClipboard.push(file);
            }
        }
    }
    if (imagesFromClipboard.length) {
        handleFileList(imagesFromClipboard);
    }
});

// Drag and drop
const dropZone = document.getElementById('dropZone');
const gridContainer = document.getElementById('gridContainer');

['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
    gridContainer.addEventListener(eventName, preventDefaults, false);
});

function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
}

['dragenter', 'dragover'].forEach(eventName => {
    gridContainer.addEventListener(eventName, () => {
        dropZone.classList.add('active');
    }, false);
});

['dragleave', 'drop'].forEach(eventName => {
    gridContainer.addEventListener(eventName, () => {
        dropZone.classList.remove('active');
    }, false);
});

gridContainer.addEventListener('drop', (e) => {
    const dt = e.dataTransfer;
    const files = dt.files;
    handleFileList(files);
}, false);

dropZone.addEventListener('click', (e) => {
    e.preventDefault();
    if (images.length > 0) return;
    openFiles();
});

// Resizer
const resizer = document.getElementById('resizer');
const imagesArea = document.getElementById('imagesArea');
const previewSplitResizer = document.getElementById('previewSplitResizer');
const previewLeft = document.getElementById('previewLeft');
const previewSplit = document.getElementById('previewSplit');
let isPreviewSplitResizing = false;
let previewSplitStartPos = 0;
let previewSplitStartSize = 0;
const MIN_PREVIEW_WIDTH = 320;
const PREVIEW_FULLSCREEN_BREAKPOINT = 700;

function getSplitBounds(containerSize, resizerSize, defaultMin = 180) {
    const maxAllowedMin = Math.floor((containerSize - resizerSize) / 2);
    const min = Math.max(0, Math.min(defaultMin, maxAllowedMin));
    const max = containerSize - resizerSize - min;
    if (max < min) {
        const fallback = Math.max(0, maxAllowedMin);
        return { min: fallback, max: fallback };
    }
    return { min, max };
}

function getMainSplitBounds() {
    const splitContainer = document.querySelector('.split-container');
    const containerWidth = splitContainer?.getBoundingClientRect().width || window.innerWidth;
    const resizerWidth = resizer ? resizer.getBoundingClientRect().width : 4;
    const maxImages = Math.max(0, containerWidth - resizerWidth - MIN_PREVIEW_WIDTH);
    let minImages = 267;
    if (maxImages < minImages) {
        minImages = Math.max(200, maxImages);
    }
    return { min: minImages, max: maxImages };
}

function clampMainSplitWidth(nextWidth) {
    const bounds = getMainSplitBounds();
    return Math.max(bounds.min, Math.min(bounds.max, nextWidth));
}

function canResizeMainPreview() {
    const previewPaneEl = document.getElementById('previewPane');
    return Boolean(previewPaneEl?.classList.contains('active'));
}

function updateMainResizerState() {
    if (!resizer) return;
    resizer.classList.toggle('disabled', !canResizeMainPreview());
}

function canResizeSplitPreview() {
    const previewPaneEl = document.getElementById('previewPane');
    const previewContent = document.getElementById('previewContent');
    return Boolean(
        previewPaneEl?.classList.contains('active')
        && previewContent?.classList.contains('split-active')
    );
}

resizer.addEventListener('mousedown', (e) => {
    if (!canResizeMainPreview()) return;
    isResizing = true;
    startX = e.clientX;
    startWidth = imagesArea.offsetWidth;
    resizer.classList.add('resizing');
    document.body.classList.add('resizing');
    e.preventDefault();
});

resizer.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    if (!canResizeMainPreview()) return;
    isResizing = true;
    startX = e.clientX;
    startWidth = imagesArea.offsetWidth;
    resizer.classList.add('resizing');
    document.body.classList.add('resizing');
    if (resizer.setPointerCapture) {
        resizer.setPointerCapture(e.pointerId);
    }
    e.preventDefault();
});

document.addEventListener('mousemove', (e) => {
    if (!isResizing) return;
    if (!canResizeMainPreview()) {
        isResizing = false;
        resizer.classList.remove('resizing');
        document.body.classList.remove('resizing');
        return;
    }
    const diff = e.clientX - startX;
    const newWidth = clampMainSplitWidth(startWidth + diff);
    imagesArea.style.flex = `0 0 ${newWidth}px`;
    updatePreviewZoomLabels();
    if (isSplitPreview) {
        updateSplitLayoutForContainer();
    }
});

document.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') return;
    if (!isResizing) return;
    if (!canResizeMainPreview()) {
        isResizing = false;
        resizer.classList.remove('resizing');
        document.body.classList.remove('resizing');
        return;
    }
    const diff = e.clientX - startX;
    const newWidth = clampMainSplitWidth(startWidth + diff);
    imagesArea.style.flex = `0 0 ${newWidth}px`;
    updatePreviewZoomLabels();
    if (isSplitPreview) {
        updateSplitLayoutForContainer();
    }
});

document.addEventListener('mouseup', () => {
    if (isResizing) {
        isResizing = false;
        resizer.classList.remove('resizing');
        document.body.classList.remove('resizing');
        updatePreviewZoomLabels();
        if (isSplitPreview) {
            updateSplitLayoutForContainer();
        }
    }
});

document.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    if (isResizing) {
        isResizing = false;
        resizer.classList.remove('resizing');
        document.body.classList.remove('resizing');
        updatePreviewZoomLabels();
        if (isSplitPreview) {
            updateSplitLayoutForContainer();
        }
    }
});

window.addEventListener('resize', () => {
    if (!canResizeMainPreview() || !imagesArea) return;
    const currentWidth = imagesArea.getBoundingClientRect().width;
    const clamped = clampMainSplitWidth(currentWidth);
    if (Math.abs(clamped - currentWidth) > 1) {
        imagesArea.style.flex = `0 0 ${clamped}px`;
    }
    updatePreviewZoomLabels();
    if (isSplitPreview) {
        updateSplitLayoutForContainer();
    }
});

if (previewSplitResizer) {
    previewSplitResizer.addEventListener('mousedown', (e) => {
        if (!previewSplit || !previewLeft) return;
        if (!canResizeSplitPreview()) return;
        isPreviewSplitResizing = true;
        if (splitOrientation === 'vertical') {
            previewSplitStartPos = e.clientY;
            previewSplitStartSize = previewLeft.getBoundingClientRect().height;
            document.body.classList.add('resizing-vertical');
        } else {
            previewSplitStartPos = e.clientX;
            previewSplitStartSize = previewLeft.getBoundingClientRect().width;
            document.body.classList.add('resizing');
        }
        previewSplitResizer.classList.add('resizing');
        e.preventDefault();
    });

    previewSplitResizer.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!previewSplit || !previewLeft) return;
        if (!canResizeSplitPreview()) return;
        isPreviewSplitResizing = true;
        if (splitOrientation === 'vertical') {
            previewSplitStartPos = e.clientY;
            previewSplitStartSize = previewLeft.getBoundingClientRect().height;
            document.body.classList.add('resizing-vertical');
        } else {
            previewSplitStartPos = e.clientX;
            previewSplitStartSize = previewLeft.getBoundingClientRect().width;
            document.body.classList.add('resizing');
        }
        previewSplitResizer.classList.add('resizing');
        if (previewSplitResizer.setPointerCapture) {
            previewSplitResizer.setPointerCapture(e.pointerId);
        }
        e.preventDefault();
    });
}

document.addEventListener('mousemove', (e) => {
    if (!isPreviewSplitResizing || !previewSplit || !previewLeft) return;
    if (!canResizeSplitPreview()) {
        isPreviewSplitResizing = false;
        previewSplitResizer?.classList.remove('resizing');
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-vertical');
        return;
    }
    const isFullscreen = isPreviewPaneFullscreen();
    if (splitOrientation === 'vertical') {
        const diff = e.clientY - previewSplitStartPos;
        const containerHeight = previewSplit.getBoundingClientRect().height;
        const resizerHeight = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().height : 6;
        const bounds = getSplitBounds(containerHeight, resizerHeight);
        let newTop = previewSplitStartSize + diff;
        newTop = Math.max(bounds.min, Math.min(bounds.max, newTop));
        previewLeft.style.flex = `0 0 ${newTop}px`;
        if (isFullscreen) {
            lastSplitPositionVerticalFullscreen = newTop;
        } else {
            lastSplitPositionVertical = newTop;
        }
    } else {
        const diff = e.clientX - previewSplitStartPos;
        const containerWidth = previewSplit.getBoundingClientRect().width;
        const resizerWidth = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().width : 6;
        const bounds = getSplitBounds(containerWidth, resizerWidth);
        let newLeft = previewSplitStartSize + diff;
        newLeft = Math.max(bounds.min, Math.min(bounds.max, newLeft));
        previewLeft.style.flex = `0 0 ${newLeft}px`;
        if (isFullscreen) {
            lastSplitPositionFullscreen = newLeft;
        } else {
            lastSplitPosition = newLeft;
        }
    }
    updatePreviewZoomLabels();
});

document.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') return;
    if (!isPreviewSplitResizing || !previewSplit || !previewLeft) return;
    if (!canResizeSplitPreview()) {
        isPreviewSplitResizing = false;
        previewSplitResizer?.classList.remove('resizing');
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-vertical');
        return;
    }
    const isFullscreen = isPreviewPaneFullscreen();
    if (splitOrientation === 'vertical') {
        const diff = e.clientY - previewSplitStartPos;
        const containerHeight = previewSplit.getBoundingClientRect().height;
        const resizerHeight = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().height : 6;
        const bounds = getSplitBounds(containerHeight, resizerHeight);
        let newTop = previewSplitStartSize + diff;
        newTop = Math.max(bounds.min, Math.min(bounds.max, newTop));
        previewLeft.style.flex = `0 0 ${newTop}px`;
        if (isFullscreen) {
            lastSplitPositionVerticalFullscreen = newTop;
        } else {
            lastSplitPositionVertical = newTop;
        }
    } else {
        const diff = e.clientX - previewSplitStartPos;
        const containerWidth = previewSplit.getBoundingClientRect().width;
        const resizerWidth = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().width : 6;
        const bounds = getSplitBounds(containerWidth, resizerWidth);
        let newLeft = previewSplitStartSize + diff;
        newLeft = Math.max(bounds.min, Math.min(bounds.max, newLeft));
        previewLeft.style.flex = `0 0 ${newLeft}px`;
        if (isFullscreen) {
            lastSplitPositionFullscreen = newLeft;
        } else {
            lastSplitPosition = newLeft;
        }
    }
    updatePreviewZoomLabels();
});

document.addEventListener('mouseup', () => {
    if (isPreviewSplitResizing) {
        isPreviewSplitResizing = false;
        previewSplitResizer?.classList.remove('resizing');
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-vertical');
        updatePreviewZoomLabels();
    }
});

document.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    if (isPreviewSplitResizing) {
        isPreviewSplitResizing = false;
        previewSplitResizer?.classList.remove('resizing');
        document.body.classList.remove('resizing');
        document.body.classList.remove('resizing-vertical');
        updatePreviewZoomLabels();
    }
});

document.addEventListener('fullscreenchange', () => {
    const previewPane = document.getElementById('previewPane');
    const isFullscreen = previewPane && document.fullscreenElement === previewPane;
    previewFullscreen = Boolean(isFullscreen);
    if (!isFullscreen && pendingClosePreviewAfterFullscreen) {
        const pending = pendingClosePreviewAfterFullscreen;
        pendingClosePreviewAfterFullscreen = null;
        finalizeClosePreview(Boolean(pending.skipSplitReset));
        return;
    }
    if (!isFullscreen && forcedFullscreenPreview && window.innerWidth < PREVIEW_FULLSCREEN_BREAKPOINT) {
        if (previewPane?.classList.contains('active')) {
            closePreview();
            return;
        }
    }
    if (isFullscreen) {
        document.body.classList.remove('preview-fullscreen');
        fullscreenSplitNeedsReset = true;
        lastSplitPositionFullscreen = null;
        lastSplitPositionVerticalFullscreen = null;
    } else {
        document.body.classList.remove('preview-fullscreen');
    }
    updatePreviewFullscreenButton();
    requestAnimationFrame(() => {
        updateSplitLayoutForContainer();
        if (isFullscreen) {
            storeCurrentSplitSize(true);
        }
        updatePreviewZoomLabels();
    });
});

// Functions
function openFiles() {
    document.getElementById('fileInput').click();
}

function openFilesFromHeader() {
    if (profilesVisible) {
        toggleProfiles();
    }
    openFiles();
}

function openAbout() {
    syncAboutCheckbox();
    document.getElementById('aboutModal').classList.add('active');
}

function closeAbout() {
    document.getElementById('aboutModal').classList.remove('active');
    persistPreferencesNoDirty();
}

function closeMobileWarning() {
    const warning = document.getElementById('mobileWarning');
    if (warning) {
        warning.remove();
    }
    const prevSuppress = suppressDirty;
    suppressDirty = true;
    persistPreferences({ mobileWarningDismissed: true });
    suppressDirty = prevSuppress;
}

function openChangelog() {
    closeAbout();
    document.getElementById('changelogModal').classList.add('active');
}

function closeChangelog() {
    document.getElementById('changelogModal').classList.remove('active');
}

function handleFiles(e) {
    handleFileList(e.target.files);
}

function isHeicExtension(filename = '') {
    return /\.(heic|heif)$/i.test(filename);
}

function isAcceptedImageFile(file) {
    const fileType = (file && file.type ? file.type : '').toLowerCase();
    if (fileType.startsWith('image/')) return true;
    return isHeicExtension(file && file.name ? file.name : '');
}

async function loadHeicToLibrary() {
    if (window.HeicTo && typeof window.HeicTo === 'function') {
        return window.HeicTo;
    }
    if (!heicToLoadPromise) {
        heicToLoadPromise = new Promise(async (resolve, reject) => {
            for (const url of HEIC_TO_IIFE_URLS) {
                try {
                    await new Promise((res, rej) => {
                        const script = document.createElement('script');
                        script.src = url;
                        script.async = true;
                        script.onload = () => res();
                        script.onerror = () => rej(new Error(`Failed to load script: ${url}`));
                        document.head.appendChild(script);
                    });
                    if (window.HeicTo && typeof window.HeicTo === 'function') {
                        resolve(window.HeicTo);
                        return;
                    }
                } catch (err) {
                    // Try next URL.
                }
            }
            reject(new Error('Unable to load heic-to browser build'));
        }).catch((err) => {
            heicToLoadPromise = null;
            throw err;
        });
    }
    return heicToLoadPromise;
}

async function browserSupportsNativeHeicDecode(file) {
    try {
        const bitmap = await createImageBitmap(file);
        bitmap.close();
        return true;
    } catch (err) {
        return false;
    }
}

async function decodeImageDimensions(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => reject(new Error('Failed to decode image dimensions'));
        img.src = src;
    });
}

async function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
        reader.readAsDataURL(file);
    });
}

async function prepareImageFileForBrowser(file) {
    const looksLikeHeic = isHeicExtension(file.name)
        || (file.type || '').toLowerCase().includes('heic')
        || (file.type || '').toLowerCase().includes('heif');
    if (!looksLikeHeic) return file;

    try {
        const heicTo = await loadHeicToLibrary();
        const isHeic = heicTo && typeof heicTo.isHeic === 'function' ? heicTo.isHeic : null;
        const confirmedHeic = isHeic ? await isHeic(file) : true;
        if (!confirmedHeic) return file;

        if (!nativeHeicSupportChecked) {
            nativeHeicSupported = await browserSupportsNativeHeicDecode(file);
            nativeHeicSupportChecked = true;
        }
        if (nativeHeicSupported) return file;

        const convertedBlob = await heicTo({
            blob: file,
            type: 'image/jpeg',
            quality: 0.92
        });
        const convertedName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        return new File([convertedBlob], convertedName, {
            type: 'image/jpeg',
            lastModified: file.lastModified
        });
    } catch (err) {
        console.warn('HEIC fallback conversion failed; using original file.', err);
        return file;
    }
}

function handleFileList(files) {
    const imageFiles = Array.from(files).filter(isAcceptedImageFile);
    if (imageFiles.length === 0) return;
    const newImages = [];
    const totalImages = imageFiles.length;
    let pending = imageFiles.length;
    let processed = 0;
    let importProgressVisible = false;
    const importProgressId = ++activeImportProgressId;
    const progress = document.getElementById('exportProgress');
    const progressBar = document.getElementById('exportProgressBar');
    const progressText = document.getElementById('exportProgressText');

    const isCurrentImportProgress = () => activeImportProgressId === importProgressId && !exportInProgress;

    const updateImportProgressUI = () => {
        if (!importProgressVisible || !isCurrentImportProgress()) return;
        const percent = totalImages > 0 ? Math.round((processed / totalImages) * 100) : 0;
        if (progressBar) {
            progressBar.style.width = `${percent}%`;
        }
        if (progressText) {
            progressText.textContent = `Importing images ${processed}/${totalImages}`;
        }
    };

    const showImportProgressUI = () => {
        if (pending <= 0 || !isCurrentImportProgress()) return;
        importProgressVisible = true;
        if (progress) {
            progress.classList.add('active');
        }
        updateImportProgressUI();
    };

    const hideImportProgressUI = (delayMs = 400) => {
        if (!importProgressVisible || !isCurrentImportProgress()) return;
        setTimeout(() => {
            if (!isCurrentImportProgress()) return;
            if (progress) {
                progress.classList.remove('active');
            }
            if (progressBar) {
                progressBar.style.width = '0%';
            }
            if (progressText) {
                progressText.textContent = '';
            }
        }, delayMs);
    };
    const importProgressTimer = window.setTimeout(showImportProgressUI, IMPORT_PROGRESS_DELAY_MS);

    const finalizeBatch = () => {
        processed += 1;
        pending -= 1;
        updateImportProgressUI();
        if (pending > 0) return;
        window.clearTimeout(importProgressTimer);
        if (importProgressVisible && isCurrentImportProgress()) {
            if (progressBar) {
                progressBar.style.width = '100%';
            }
            if (progressText) {
                progressText.textContent = 'Import complete';
            }
            hideImportProgressUI();
        }
        renderImages();
        updateExportButton();
        syncImagesVisibility(true);
        if (newImages.length > 0) {
            setDirtyState(true);
        }
    };

    imageFiles.forEach(async (file) => {
        try {
            const preparedFile = await prepareImageFileForBrowser(file);
            const src = await readFileAsDataURL(preparedFile);
            const dimensions = await decodeImageDimensions(src);
            const id = Date.now() + Math.random();
            const entry = {
                id,
                name: file.name,
                src,
                file: preparedFile,
                width: dimensions.width,
                height: dimensions.height,
                crop: calculateInitialCrop(dimensions.width, dimensions.height),
                justAdded: true
            };
            images.push(entry);
            newImages.push(entry);
        } catch (err) {
            console.warn(`Failed to load image: ${file.name}`, err);
        } finally {
            finalizeBatch();
        }
    });

    if (dropZone) {
        dropZone.classList.add('hidden');
    }
    syncImagesVisibility(true);
}

function calculateInitialCrop(imgWidth, imgHeight) {
    if (!isAspectLockedMode()) {
        return {
            x: 0,
            y: 0,
            width: imgWidth,
            height: imgHeight
        };
    }

    const imgAspect = imgWidth / imgHeight;
    const targetAspect = Number.isFinite(currentAspectRatio) && currentAspectRatio > 0
        ? currentAspectRatio
        : imgAspect;

    if (imgAspect > targetAspect) {
        // Image is wider - fit height
        const cropWidth = imgHeight * targetAspect;
        return {
            x: (imgWidth - cropWidth) / 2,
            y: 0,
            width: cropWidth,
            height: imgHeight
        };
    } else {
        // Image is taller - fit width
        const cropHeight = imgWidth / targetAspect;
        return {
            x: 0,
            y: (imgHeight - cropHeight) / 2,
            width: imgWidth,
            height: cropHeight
        };
    }
}

function renderImages() {
    const grid = document.getElementById('imagesGrid');
    grid.innerHTML = '';
    updateGridSize();

    images.forEach(img => {
        const card = createImageCard(img);
        grid.appendChild(card);
        img.justAdded = false;
    });

    updateGridInfoLabels();
    syncImagesVisibility();
    updateFilenameTipVisibility();
}

function refreshGridAfterResume() {
    if (images.length === 0) return;
    const container = document.getElementById('gridContainer');
    const scrollTop = container ? container.scrollTop : 0;
    const scrollLeft = container ? container.scrollLeft : 0;
    renderImages();
    if (container) {
        container.scrollTop = scrollTop;
        container.scrollLeft = scrollLeft;
    }
    updateSelectedCard();
}

function scheduleGridRefreshAfterResume() {
    if (resumeGridRefreshTimer) {
        clearTimeout(resumeGridRefreshTimer);
    }
    resumeGridRefreshTimer = setTimeout(() => {
        resumeGridRefreshTimer = null;
        refreshGridAfterResume();
    }, 120);
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function getGridLabelForImage(img) {
    if (!img) return '';
    const safeName = escapeHtml(img.name);
    if (!showGridSizeInfo) return safeName;
    const { width, height } = getExportOutputDimensions(img);
    const inputWidth = img.width;
    const inputHeight = img.height;
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
        return safeName;
    }
    return `${inputWidth}<span class="image-dimensions-x">x</span>${inputHeight}<span class="image-dimensions-x">-></span>${width}<span class="image-dimensions-x">x</span>${height} ${safeName}`;
}

function updateGridInfoLabelForImage(img) {
    const grid = document.getElementById('imagesGrid');
    if (!grid || !img) return;
    const card = grid.querySelector(`[data-image-id="${img.id}"]`);
    if (!card) return;
    const nameText = card.querySelector('.image-name-text');
    if (!nameText) return;
    nameText.innerHTML = getGridLabelForImage(img);
}

function updateGridInfoLabels() {
    const toggleBtn = document.getElementById('gridInfoToggle');
    if (toggleBtn) {
        toggleBtn.classList.toggle('active', showGridSizeInfo);
        toggleBtn.setAttribute('aria-pressed', String(showGridSizeInfo));
        toggleBtn.title = showGridSizeInfo ? 'Show filenames' : 'Show size info';
    }
    images.forEach(img => updateGridInfoLabelForImage(img));
}

function toggleGridInfo() {
    showGridSizeInfo = !showGridSizeInfo;
    persistPreferencesNoDirty({ showGridSizeInfo });
    updateGridInfoLabels();
}

function updateFilenameTipVisibility() {
    const tip = document.getElementById('filenameTip');
    if (!tip) return;
    const shouldShow = showFilenameTip && images.length > 0 && !profilesVisible;
    tip.classList.toggle('hidden', !shouldShow);
    if (shouldShow) {
        positionFilenameTip();
    }
}

function positionFilenameTip() {
    const tip = document.getElementById('filenameTip');
    const grid = document.getElementById('imagesGrid');
    const container = document.getElementById('gridContainer');
    if (!tip || !grid || !container) return;
    const firstCard = grid.querySelector('.image-card');
    if (!firstCard) {
        tip.classList.add('hidden');
        return;
    }
    const nameEl = firstCard.querySelector('.image-name');
    if (!nameEl) {
        tip.classList.add('hidden');
        return;
    }

    tip.classList.remove('hidden');
    const containerRect = container.getBoundingClientRect();
    const cardRect = firstCard.getBoundingClientRect();
    const nameRect = nameEl.getBoundingClientRect();

    const desiredTop = (cardRect.bottom - containerRect.top) + 8 + container.scrollTop;
    const desiredLeft = (nameRect.left - containerRect.left) + container.scrollLeft;

    const tipWidth = tip.offsetWidth || 260;
    const containerWidth = container.clientWidth || containerRect.width;
    const padding = 12;
    const clampedLeft = Math.max(padding, Math.min(desiredLeft, containerWidth - tipWidth - padding));

    tip.style.top = `${Math.round(desiredTop)}px`;
    tip.style.left = `${Math.round(clampedLeft)}px`;

    const arrowCenter = (nameRect.left - containerRect.left) + (nameRect.width / 2) + container.scrollLeft;
    const arrowOffset = Math.max(16, Math.min(tipWidth - 16, arrowCenter - clampedLeft));
    tip.style.setProperty('--arrow-left', `${Math.round(arrowOffset)}px`);
}

function dismissFilenameTip() {
    showFilenameTip = false;
    persistPreferencesNoDirty({ showFilenameTip: false });
    updateFilenameTipVisibility();
}

function getResizeModeInfoText(mode = getResizeMode()) {
    switch (mode) {
        case RESIZE_MODES.FIXED_ASPECT:
            return 'Export size locked to aspect ratio.';
        case RESIZE_MODES.FIXED_ASPECT_CROP:
            return 'Fixed aspect ratio crop at 100% (no resize).';
        case RESIZE_MODES.STATIC_SIZE:
            return 'Force exact output dimensions.';
        case RESIZE_MODES.FIXED_WIDTH:
            return 'Lock width; height derives from crop.';
        case RESIZE_MODES.FIXED_HEIGHT:
            return 'Lock height; width derives from crop.';
        case RESIZE_MODES.SCALE_TO_FIT:
            return 'Resize to fit within max width/height; crop freely.';
        case RESIZE_MODES.FREE_CROP:
            return 'Crop freely; no resize.';
        case RESIZE_MODES.SCALED:
            return 'Scale by percent; no crop overlay.';
        case RESIZE_MODES.NO_CROP:
            return 'Convert only; no crop or resize.';
        default:
            return '';
    }
}

function updateResizeModeInfoText() {
    const infoText = document.getElementById('resizeModeInfoText');
    if (!infoText) return;
    const text = getResizeModeInfoText();
    infoText.textContent = text || ' ';
}

function createImageCard(img) {
    const card = document.createElement('div');
    card.className = 'image-card';
    if (img.justAdded) {
        card.classList.add('animate-in');
        card.addEventListener('animationend', () => {
            card.classList.remove('animate-in');
        }, { once: true });
    }
    card.dataset.imageId = img.id;
    if (selectedImageId === img.id) {
        card.classList.add('selected');
    }
    card.addEventListener('click', (e) => {
        if (e.target.closest('.image-remove-btn')) return;
        if (e.target.closest('.crop-box')) return;
        showPreview(img.id);
    });

    const container = document.createElement('div');
    container.className = 'image-preview-container';

    const wrapper = document.createElement('div');
    wrapper.className = 'image-preview-wrapper';

    const imageContainer = document.createElement('div');
    imageContainer.style.position = 'relative';
    imageContainer.style.width = '100%';
    imageContainer.style.height = '100%';
    imageContainer.style.lineHeight = '0';

    const image = document.createElement('img');
    image.className = 'image-preview';
    image.style.transform = 'scale(1)';
    image.style.position = 'relative';

    imageContainer.appendChild(image);

    // Add crop box after image loads
    image.onload = () => {
        attachCropBox(imageContainer, image, img, false);
    };
    image.src = img.src;

    if (image.complete && image.naturalWidth > 0) {
        requestAnimationFrame(() => {
            attachCropBox(imageContainer, image, img, false);
        });
    }

    wrapper.appendChild(imageContainer);
    container.appendChild(wrapper);

    const name = document.createElement('div');
    name.className = 'image-name';
    const nameText = document.createElement('span');
    nameText.className = 'image-name-text';
    nameText.innerHTML = getGridLabelForImage(img);
    const removeBtn = document.createElement('button');
    removeBtn.className = 'image-remove-btn';
    removeBtn.title = 'Remove image';
    removeBtn.textContent = '🗑';
    removeBtn.onclick = (e) => {
        e.stopPropagation();
        removeImage(img.id);
    };
    name.appendChild(nameText);
    name.appendChild(removeBtn);

    card.appendChild(container);
    card.appendChild(name);

    return card;
}

function createCropBox(img, isPreview = false) {
    const cropBox = document.createElement('div');
    cropBox.className = 'crop-box';

    // Calculate crop box position and size
    updateCropBoxStyle(cropBox, img, isPreview);

    // Create handles
    ['nw', 'ne', 'sw', 'se'].forEach(pos => {
        const handle = document.createElement('div');
        handle.className = `crop-handle ${pos}`;
        cropBox.appendChild(handle);

        makeDraggable(handle, img, pos, isPreview);
    });

    // Make crop box draggable
    makeBoxDraggable(cropBox, img, isPreview);

    return cropBox;
}

function attachCropBox(imageContainer, imageElement, img, isPreview) {
    if (!shouldShowCropOverlay()) return;
    if (!imageContainer || imageContainer.querySelector('.crop-box')) return;
    const cropBox = createCropBox(img, isPreview);
    imageContainer.appendChild(cropBox);
    requestAnimationFrame(() => {
        updateCropBoxStyle(cropBox, img, isPreview);
    });

    if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
            updateCropBoxStyle(cropBox, img, isPreview);
        });
        ro.observe(imageElement);
        cropBox._resizeObserver = ro;
    }
}

function updateSelectedCard() {
    const grid = document.getElementById('imagesGrid');
    if (!grid) return;
    grid.querySelectorAll('.image-card').forEach(card => {
        const id = Number(card.dataset.imageId);
        if (selectedImageId && id === selectedImageId) {
            card.classList.add('selected');
        } else {
            card.classList.remove('selected');
        }
    });
}

function updateGridCropBox(img) {
    if (!shouldShowCropOverlay()) return;
    const grid = document.getElementById('imagesGrid');
    if (!grid) return;
    const card = grid.querySelector(`[data-image-id="${img.id}"]`);
    if (!card) return;
    const imageElement = card.querySelector('.image-preview');
    if (!imageElement) return;
    const imageContainer = imageElement.parentElement;
    if (!imageContainer) return;
    if (!imageContainer.querySelector('.crop-box')) {
        attachCropBox(imageContainer, imageElement, img, false);
    }
    const cropBox = imageContainer.querySelector('.crop-box');
    if (cropBox) {
        updateCropBoxStyle(cropBox, img, false);
    }
}

function syncImagesVisibility(forceHideDropZone = false) {
    const grid = document.getElementById('imagesGrid');
    const dropZone = document.getElementById('dropZone');
    if (grid) {
        grid.style.display = profilesVisible ? 'none' : 'grid';
    }
    if (dropZone) {
        dropZone.style.display = profilesVisible ? 'none' : '';
        dropZone.classList.toggle('hidden', forceHideDropZone || images.length > 0);
    }
}

function updateAllCropBoxes() {
    if (!shouldShowCropOverlay()) return;
    images.forEach(img => updateGridCropBox(img));
    if (selectedImageId) {
        const previewWrapper = document.getElementById('previewWrapper');
        if (!previewWrapper) return;
        const img = images.find(i => i.id === selectedImageId);
        if (!img) return;
        const imageElement = previewWrapper.querySelector('.preview-image');
        const imageContainer = imageElement ? imageElement.parentElement : null;
        if (imageElement && imageContainer && !imageContainer.querySelector('.crop-box')) {
            attachCropBox(imageContainer, imageElement, img, true);
        }
        const cropBox = previewWrapper.querySelector('.crop-box');
        if (cropBox) {
            updateCropBoxStyle(cropBox, img, true);
        }
    }
}

function updateAspectInputsFromCurrent() {
    const aspectW = document.getElementById('aspectW');
    const aspectH = document.getElementById('aspectH');
    if (!aspectW || !aspectH) return;
    const width = parseInt(document.getElementById('exportWidth').value);
    const height = parseInt(document.getElementById('exportHeight').value);
    if (Number.isFinite(width) && Number.isFinite(height) && height !== 0) {
        const { arW, arH } = aspectRatioExact(width, height);
        if (parseFloat(aspectW.value) !== arW) {
            aspectW.value = arW;
        }
        if (parseFloat(aspectH.value) !== arH) {
            aspectH.value = arH;
        }
        updateActiveAspectButton(null, arW, arH);
    }
    updateAspectMismatchUI();
}

function getAspectMismatchState() {
    if (getResizeMode() !== RESIZE_MODES.FIXED_ASPECT) {
        return { mismatch: false, rounded: false, classification: 'exact' };
    }

    const exportWidth = parseInt(document.getElementById('exportWidth')?.value, 10);
    const exportHeight = parseInt(document.getElementById('exportHeight')?.value, 10);
    const aspectW = parseFloat(document.getElementById('aspectW')?.value);
    const aspectH = parseFloat(document.getElementById('aspectH')?.value);

    if (!Number.isFinite(exportWidth) || !Number.isFinite(exportHeight) || exportWidth <= 0 || exportHeight <= 0) {
        return { mismatch: false, rounded: false, classification: 'exact' };
    }
    if (!Number.isFinite(aspectW) || !Number.isFinite(aspectH) || aspectW <= 0 || aspectH <= 0) {
        return { mismatch: false, rounded: false, classification: 'exact' };
    }

    const classification = classifyAspect(
        exportWidth,
        exportHeight,
        Math.round(aspectW),
        Math.round(aspectH)
    );
    const mismatch = classification === 'different';
    const rounded = classification.startsWith('rounded_');
    return { mismatch, rounded, classification };
}

function updateAspectMismatchUI() {
    const warning = document.getElementById('aspectMismatchWarning');
    const roundingInfo = document.getElementById('aspectRoundingInfo');
    if (!warning || !roundingInfo) return;

    const { mismatch, rounded } = getAspectMismatchState();
    warning.classList.toggle('visible', mismatch);
    roundingInfo.classList.toggle('visible', rounded);
}

function applyStaticSizeAspect() {
    const width = parseInt(document.getElementById('exportWidth').value);
    const height = parseInt(document.getElementById('exportHeight').value);
    if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) return;
    currentAspectRatio = width / height;
    images.forEach(img => {
        img.crop = calculateInitialCrop(img.width, img.height);
    });
    updateAllCropBoxes();
    updateAspectMismatchUI();
}

function applyAspectRatioFromInputs() {
    const mode = getResizeMode();
    if (mode !== RESIZE_MODES.FIXED_ASPECT && mode !== RESIZE_MODES.FIXED_ASPECT_CROP) return;
    const aspectW = parseFloat(document.getElementById('aspectW').value);
    const aspectH = parseFloat(document.getElementById('aspectH').value);
    if (!Number.isFinite(aspectW) || !Number.isFinite(aspectH) || aspectH <= 0) return;
    currentAspectRatio = aspectW / aspectH;
    images.forEach(img => {
        img.crop = calculateInitialCrop(img.width, img.height);
    });
    if (mode === RESIZE_MODES.FIXED_ASPECT || mode === RESIZE_MODES.FIXED_ASPECT_CROP) {
        const width = parseInt(document.getElementById('exportWidth').value, 10);
        if (Number.isFinite(width) && width > 0) {
            const height = Math.round(width / currentAspectRatio);
            document.getElementById('exportHeight').value = height;
        }
    }
    updateActiveAspectButton(null, aspectW, aspectH);
    updateAspectMismatchUI();
    persistPreferences({ aspectRatio: { width: aspectW, height: aspectH } });
    bumpExportSettingsChange();
    markProfileModified();
    updateAllCropBoxes();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

function updatePreviewLayoutButtons() {
    const singleBtn = document.getElementById('previewLayoutSingle');
    const horizontalBtn = document.getElementById('previewLayoutHorizontal');
    const verticalBtn = document.getElementById('previewLayoutVertical');
    if (singleBtn) {
        singleBtn.classList.toggle('active', !isSplitPreview);
        singleBtn.setAttribute('aria-pressed', String(!isSplitPreview));
    }
    if (horizontalBtn) {
        horizontalBtn.classList.toggle('active', isSplitPreview && splitOrientation === 'horizontal');
        horizontalBtn.setAttribute('aria-pressed', String(isSplitPreview && splitOrientation === 'horizontal'));
    }
    if (verticalBtn) {
        verticalBtn.classList.toggle('active', isSplitPreview && splitOrientation === 'vertical');
        verticalBtn.setAttribute('aria-pressed', String(isSplitPreview && splitOrientation === 'vertical'));
    }
}

function updatePreviewFullscreenButton() {
    const btn = document.getElementById('previewFullscreenBtn');
    if (!btn) return;
    const isFullscreen = Boolean(document.fullscreenElement);
    const isActive = isFullscreen || previewFullscreen;
    btn.classList.toggle('active', isActive);
    btn.textContent = isActive ? '⤡' : '⤢';
    btn.title = isActive ? 'Exit full preview (spacebar)' : 'Expand preview (spacebar)';
}

function isPreviewPaneFullscreen() {
    const previewPane = document.getElementById('previewPane');
    return Boolean(previewFullscreen || (previewPane && document.fullscreenElement === previewPane));
}

function storeCurrentSplitSize(isFullscreen) {
    if (!isSplitPreview || !previewLeft || !previewSplit) return;
    if (splitOrientation === 'vertical') {
        const containerHeight = previewSplit.getBoundingClientRect().height;
        const resizerHeight = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().height : 6;
        const bounds = getSplitBounds(containerHeight, resizerHeight);
        const current = previewLeft.getBoundingClientRect().height;
        if (!Number.isFinite(current) || current <= 0) return;
        const clamped = Math.max(bounds.min, Math.min(bounds.max, current));
        if (isFullscreen) {
            lastSplitPositionVerticalFullscreen = clamped;
        } else {
            lastSplitPositionVertical = clamped;
        }
        return;
    }
    const containerWidth = previewSplit.getBoundingClientRect().width;
    const resizerWidth = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().width : 6;
    const bounds = getSplitBounds(containerWidth, resizerWidth);
    const current = previewLeft.getBoundingClientRect().width;
    if (!Number.isFinite(current) || current <= 0) return;
    const clamped = Math.max(bounds.min, Math.min(bounds.max, current));
    if (isFullscreen) {
        lastSplitPositionFullscreen = clamped;
    } else {
        lastSplitPosition = clamped;
    }
}

function updateSplitLayoutForContainer() {
    if (!isSplitPreview || !previewLeft || !previewSplit) return;
    const isFullscreen = isPreviewPaneFullscreen();
    if (splitOrientation === 'vertical') {
        const containerHeight = previewSplit.getBoundingClientRect().height;
        const resizerHeight = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().height : 6;
        const bounds = getSplitBounds(containerHeight, resizerHeight);
        let top;
        if (isFullscreen && fullscreenSplitNeedsReset) {
            top = Math.floor((containerHeight - resizerHeight) / 2);
            lastSplitPositionVerticalFullscreen = top;
            fullscreenSplitNeedsReset = false;
        } else {
            const stored = isFullscreen ? lastSplitPositionVerticalFullscreen : lastSplitPositionVertical;
            const current = previewLeft.getBoundingClientRect().height;
            const fallback = Number.isFinite(current) && current > 0 ? current : null;
            top = stored ?? fallback ?? Math.floor((containerHeight - resizerHeight) / 2);
        }
        top = Math.max(bounds.min, Math.min(bounds.max, top));
        previewLeft.style.flex = `0 0 ${top}px`;
    } else {
        const containerWidth = previewSplit.getBoundingClientRect().width;
        const resizerWidth = previewSplitResizer ? previewSplitResizer.getBoundingClientRect().width : 6;
        const bounds = getSplitBounds(containerWidth, resizerWidth);
        let left;
        if (isFullscreen && fullscreenSplitNeedsReset) {
            left = Math.floor((containerWidth - resizerWidth) / 2);
            lastSplitPositionFullscreen = left;
            fullscreenSplitNeedsReset = false;
        } else {
            const stored = isFullscreen ? lastSplitPositionFullscreen : lastSplitPosition;
            const current = previewLeft.getBoundingClientRect().width;
            const fallback = Number.isFinite(current) && current > 0 ? current : null;
            left = stored ?? fallback ?? Math.floor((containerWidth - resizerWidth) / 2);
        }
        left = Math.max(bounds.min, Math.min(bounds.max, left));
        previewLeft.style.flex = `0 0 ${left}px`;
    }
}

function setSplitPreview(enabled, preserveMode = false, orientation = splitOrientation) {
    isSplitPreview = enabled;
    if (enabled) {
        splitOrientation = orientation === 'vertical' ? 'vertical' : 'horizontal';
    }
    exportPreviewRequestId += 1;
    const previewContent = document.getElementById('previewContent');
    if (previewContent) {
        previewContent.classList.toggle('split-active', enabled);
        previewContent.classList.toggle('split-vertical', enabled && splitOrientation === 'vertical');
    }
    updateTestExportButton();
    updatePreviewLayoutButtons();
    if (enabled) {
        updateSplitLayoutForContainer();
        if (!preserveMode) {
            lastExportPreviewMode = splitOrientation === 'vertical' ? 'split-vertical' : 'split-horizontal';
            persistPreferencesNoDirty({ lastExportPreviewMode });
        }
        ensureExportPreviewPlaceholder();
        if (selectedImageId) {
            const img = images.find(i => i.id === selectedImageId);
            if (img) {
                openExportPreview(img, { preserveImage: true });
            }
        }
    } else {
        closeExportPreview(preserveMode);
        if (previewLeft) {
            previewLeft.style.flex = '';
        }
        if (!preserveMode) {
            lastExportPreviewMode = lastExportPreviewMode.startsWith('split') ? 'none' : lastExportPreviewMode;
            persistPreferencesNoDirty({ lastExportPreviewMode });
        }
    }
    updatePreviewZoomLabels();
}

function setPreviewLayout(mode, preserveMode = false) {
    if (mode === 'single') {
        if (!isSplitPreview) return;
        setSplitPreview(false, preserveMode);
        return;
    }
    const orientation = mode === 'split-vertical' ? 'vertical' : 'horizontal';
    if (isSplitPreview && splitOrientation === orientation) return;
    setSplitPreview(true, preserveMode, orientation);
}

function toggleSplitPreview() {
    setPreviewLayout(isSplitPreview ? 'single' : 'split-horizontal');
}

function togglePreviewFullscreen() {
    const previewPane = document.getElementById('previewPane');
    if (!previewPane || !previewPane.classList.contains('active')) {
        previewFullscreen = false;
        document.body.classList.remove('preview-fullscreen');
        updatePreviewFullscreenButton();
        return;
    }
    if (document.fullscreenElement) {
        if (document.fullscreenElement === previewPane) {
            storeCurrentSplitSize(true);
            document.exitFullscreen();
        }
        return;
    }
    if (previewPane.requestFullscreen) {
        storeCurrentSplitSize(false);
        fullscreenSplitNeedsReset = true;
        lastSplitPositionFullscreen = null;
        lastSplitPositionVerticalFullscreen = null;
        previewPane.requestFullscreen().catch(() => {
            const wasFullscreen = previewFullscreen;
            if (wasFullscreen) {
                storeCurrentSplitSize(true);
            } else {
                storeCurrentSplitSize(false);
            }
            previewFullscreen = !previewFullscreen;
            document.body.classList.toggle('preview-fullscreen', previewFullscreen);
            updatePreviewFullscreenButton();
            updatePreviewZoomLabels();
            updateSplitLayoutForContainer();
            if (!wasFullscreen && previewFullscreen) {
                fullscreenSplitNeedsReset = true;
                lastSplitPositionFullscreen = null;
                lastSplitPositionVerticalFullscreen = null;
                storeCurrentSplitSize(true);
            }
        });
        return;
    }
    const wasFullscreen = previewFullscreen;
    if (wasFullscreen) {
        storeCurrentSplitSize(true);
    } else {
        storeCurrentSplitSize(false);
    }
    previewFullscreen = !previewFullscreen;
    document.body.classList.toggle('preview-fullscreen', previewFullscreen);
    if (previewFullscreen) {
        fullscreenSplitNeedsReset = true;
        lastSplitPositionFullscreen = null;
        lastSplitPositionVerticalFullscreen = null;
    }
    updatePreviewFullscreenButton();
    updateSplitLayoutForContainer();
    updatePreviewZoomLabels();
    if (!wasFullscreen && previewFullscreen) {
        storeCurrentSplitSize(true);
    }
}

function updateCropBoxStyle(cropBox, img, isPreview) {
    const imageContainer = cropBox.parentElement;
    if (!imageContainer) return;

    const imageElement = imageContainer.querySelector('.image-preview, .preview-image');
    if (!imageElement) return;

    // Wait for image to be fully loaded
    if (!imageElement.complete || imageElement.naturalWidth === 0) {
        setTimeout(() => updateCropBoxStyle(cropBox, img, isPreview), 50);
        return;
    }

    const metrics = getImageDisplayMetrics(imageContainer, img);
    if (!metrics) {
        requestAnimationFrame(() => updateCropBoxStyle(cropBox, img, isPreview));
        return;
    }

    const { scale, offsetX, offsetY } = metrics;

    // Position crop box absolutely relative to the image container
    cropBox.style.position = 'absolute';
    cropBox.style.left = `${offsetX + img.crop.x * scale}px`;
    cropBox.style.top = `${offsetY + img.crop.y * scale}px`;
    cropBox.style.width = `${img.crop.width * scale}px`;
    cropBox.style.height = `${img.crop.height * scale}px`;
    if (!isPreview && showGridSizeInfo) {
        updateGridInfoLabelForImage(img);
    }
}

function getImageDisplayMetrics(imageContainer, img) {
    const containerRect = imageContainer.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    if (!containerWidth || !containerHeight) return null;

    const imgAspect = img.width / img.height;
    const containerAspect = containerWidth / containerHeight;

    let displayedWidth;
    let displayedHeight;

    if (imgAspect > containerAspect) {
        displayedWidth = containerWidth;
        displayedHeight = containerWidth / imgAspect;
    } else {
        displayedHeight = containerHeight;
        displayedWidth = containerHeight * imgAspect;
    }

    const scale = displayedWidth / img.width;
    const offsetX = (containerWidth - displayedWidth) / 2;
    const offsetY = (containerHeight - displayedHeight) / 2;

    return { scale, offsetX, offsetY };
}

function makeBoxDraggable(box, img, isPreview) {
    let isDragging = false;
    let startX, startY, startCropX, startCropY;
    let didChange = false;
    let lastChangeId = 0;
    let activePointerId = null;
    let activeInput = null;

    box.addEventListener('mousedown', (e) => {
        if (e.target.classList.contains('crop-handle')) return;

        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        startX = e.clientX;
        startY = e.clientY;
        startCropX = img.crop.x;
        startCropY = img.crop.y;

        e.preventDefault();
    });

    box.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        if (e.target.classList.contains('crop-handle')) return;
        if (activeInput && activeInput !== 'pointer') return;

        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        activePointerId = e.pointerId;
        activeInput = 'pointer';
        startX = e.clientX;
        startY = e.clientY;
        startCropX = img.crop.x;
        startCropY = img.crop.y;

        box.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    });

    box.addEventListener('touchstart', (e) => {
        if (e.target.classList.contains('crop-handle')) return;
        if (activeInput && activeInput !== 'touch') return;
        const touch = e.touches?.[0];
        if (!touch) return;

        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        activePointerId = null;
        activeInput = 'touch';
        startX = touch.clientX;
        startY = touch.clientY;
        startCropX = img.crop.x;
        startCropY = img.crop.y;

        e.preventDefault();
    }, { passive: false });

    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        if (activeInput && activeInput !== 'mouse') return;

        const imageContainer = box.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;

        let newX = startCropX + dx;
        let newY = startCropY + dy;

        // Constrain to image bounds
        newX = Math.max(0, Math.min(newX, img.width - img.crop.width));
        newY = Math.max(0, Math.min(newY, img.height - img.crop.height));

        img.crop.x = newX;
        img.crop.y = newY;
        if (newX !== startCropX || newY !== startCropY) {
            didChange = true;
            lastChangeId = ++cropChangeId;
            img._lastCropChangeId = lastChangeId;
        }

        updateCropBoxStyle(box, img, isPreview);

        if (isPreview) {
            updateGridCropIfVisible(img);
            if (didChange) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        } else {
            if (selectedImageId === img.id) {
                updatePreviewCropIfVisible(img);
            }
            if (didChange && selectedImageId === img.id) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        }
    });

    document.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        if (activeInput && activeInput !== 'pointer') return;

        const imageContainer = box.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;

        let newX = startCropX + dx;
        let newY = startCropY + dy;

        // Constrain to image bounds
        newX = Math.max(0, Math.min(newX, img.width - img.crop.width));
        newY = Math.max(0, Math.min(newY, img.height - img.crop.height));

        img.crop.x = newX;
        img.crop.y = newY;
        if (newX !== startCropX || newY !== startCropY) {
            didChange = true;
            lastChangeId = ++cropChangeId;
            img._lastCropChangeId = lastChangeId;
        }

        updateCropBoxStyle(box, img, isPreview);

        if (isPreview) {
            updateGridCropIfVisible(img);
            if (didChange) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        } else {
            if (selectedImageId === img.id) {
                updatePreviewCropIfVisible(img);
            }
            if (didChange && selectedImageId === img.id) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        }
    });

    document.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        const touch = e.touches?.[0];
        if (!touch) return;

        const imageContainer = box.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (touch.clientX - startX) / scale;
        const dy = (touch.clientY - startY) / scale;

        let newX = startCropX + dx;
        let newY = startCropY + dy;

        // Constrain to image bounds
        newX = Math.max(0, Math.min(newX, img.width - img.crop.width));
        newY = Math.max(0, Math.min(newY, img.height - img.crop.height));

        img.crop.x = newX;
        img.crop.y = newY;
        if (newX !== startCropX || newY !== startCropY) {
            didChange = true;
            lastChangeId = ++cropChangeId;
            img._lastCropChangeId = lastChangeId;
        }

        updateCropBoxStyle(box, img, isPreview);

        if (isPreview) {
            updateGridCropIfVisible(img);
            if (didChange) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        } else {
            if (selectedImageId === img.id) {
                updatePreviewCropIfVisible(img);
            }
            if (didChange && selectedImageId === img.id) {
                scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
            }
        }

        e.preventDefault();
    }, { passive: false });

    document.addEventListener('mouseup', () => {
        if (isDragging) {
            isDragging = false;
            activePointerId = null;
            activeInput = null;
            if (!isPreview) {
                updatePreviewCropIfVisible(img);
                if (didChange && selectedImageId === img.id) {
                    if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                        scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                    }
                }
            } else {
                updateGridCropIfVisible(img);
                if (didChange) {
                    if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                        scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                    }
                }
            }
        }
    });

    document.addEventListener('pointerup', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        if (activeInput && activeInput !== 'pointer') return;

        isDragging = false;
        activePointerId = null;
        activeInput = null;
        if (!isPreview) {
            updatePreviewCropIfVisible(img);
            if (didChange && selectedImageId === img.id) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        } else {
            updateGridCropIfVisible(img);
            if (didChange) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        }
    });

    document.addEventListener('pointercancel', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
    });

    document.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
        if (!isPreview) {
            updatePreviewCropIfVisible(img);
            if (didChange && selectedImageId === img.id) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        } else {
            updateGridCropIfVisible(img);
            if (didChange) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        }
    });

    document.addEventListener('touchcancel', () => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
    });
}

function makeDraggable(handle, img, position, isPreview) {
    let isDragging = false;
    let startX, startY, startCrop;
    let didChange = false;
    let lastChangeId = 0;
    let activePointerId = null;
    let activeInput = null;

    handle.addEventListener('mousedown', (e) => {
        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        handle.classList.add('dragging');
        startX = e.clientX;
        startY = e.clientY;
        startCrop = { ...img.crop };

        e.preventDefault();
        e.stopPropagation();
    });

    handle.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        if (activeInput && activeInput !== 'pointer') return;
        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        activePointerId = e.pointerId;
        activeInput = 'pointer';
        handle.classList.add('dragging');
        startX = e.clientX;
        startY = e.clientY;
        startCrop = { ...img.crop };

        handle.setPointerCapture?.(e.pointerId);
        e.preventDefault();
        e.stopPropagation();
    });

    handle.addEventListener('touchstart', (e) => {
        if (activeInput && activeInput !== 'touch') return;
        const touch = e.touches?.[0];
        if (!touch) return;
        isDragging = true;
        didChange = false;
        lastChangeId = 0;
        activePointerId = null;
        activeInput = 'touch';
        handle.classList.add('dragging');
        startX = touch.clientX;
        startY = touch.clientY;
        startCrop = { ...img.crop };

        e.preventDefault();
        e.stopPropagation();
    }, { passive: false });

    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        if (activeInput && activeInput !== 'mouse') return;

        const cropBox = handle.parentElement;
        const imageContainer = cropBox.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;

        let newCrop = { ...startCrop };

        // Adjust based on handle position
        if (position.includes('n')) {
            newCrop.y = startCrop.y + dy;
            newCrop.height = startCrop.height - dy;
        }
        if (position.includes('s')) {
            newCrop.height = startCrop.height + dy;
        }
        if (position.includes('w')) {
            newCrop.x = startCrop.x + dx;
            newCrop.width = startCrop.width - dx;
        }
        if (position.includes('e')) {
            newCrop.width = startCrop.width + dx;
        }

        // Maintain aspect ratio
        if (isAspectLockedMode()) {
            newCrop = constrainCropWithAspect(newCrop, startCrop, position, img, currentAspectRatio);
        }

        // Constrain to image bounds
        newCrop = clampCropToBounds(newCrop, img);

        // Minimum size
        if (newCrop.width > 20 && newCrop.height > 20) {
            img.crop = newCrop;
            if (
                newCrop.x !== startCrop.x ||
                newCrop.y !== startCrop.y ||
                newCrop.width !== startCrop.width ||
                newCrop.height !== startCrop.height
            ) {
                didChange = true;
                lastChangeId = ++cropChangeId;
                img._lastCropChangeId = lastChangeId;
            }
            updateCropBoxStyle(cropBox, img, isPreview);

            if (isPreview) {
                updateGridCropIfVisible(img);
                if (didChange) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            } else {
                if (selectedImageId === img.id) {
                    updatePreviewCropIfVisible(img);
                }
                if (didChange && selectedImageId === img.id) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            }
        }
    });

    document.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        if (activeInput && activeInput !== 'pointer') return;

        const cropBox = handle.parentElement;
        const imageContainer = cropBox.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;

        let newCrop = { ...startCrop };

        // Adjust based on handle position
        if (position.includes('n')) {
            newCrop.y = startCrop.y + dy;
            newCrop.height = startCrop.height - dy;
        }
        if (position.includes('s')) {
            newCrop.height = startCrop.height + dy;
        }
        if (position.includes('w')) {
            newCrop.x = startCrop.x + dx;
            newCrop.width = startCrop.width - dx;
        }
        if (position.includes('e')) {
            newCrop.width = startCrop.width + dx;
        }

        // Maintain aspect ratio
        if (isAspectLockedMode()) {
            newCrop = constrainCropWithAspect(newCrop, startCrop, position, img, currentAspectRatio);
        }

        // Constrain to image bounds
        newCrop = clampCropToBounds(newCrop, img);

        // Minimum size
        if (newCrop.width > 20 && newCrop.height > 20) {
            img.crop = newCrop;
            if (
                newCrop.x !== startCrop.x ||
                newCrop.y !== startCrop.y ||
                newCrop.width !== startCrop.width ||
                newCrop.height !== startCrop.height
            ) {
                didChange = true;
                lastChangeId = ++cropChangeId;
                img._lastCropChangeId = lastChangeId;
            }
            updateCropBoxStyle(cropBox, img, isPreview);

            if (isPreview) {
                updateGridCropIfVisible(img);
                if (didChange) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            } else {
                if (selectedImageId === img.id) {
                    updatePreviewCropIfVisible(img);
                }
                if (didChange && selectedImageId === img.id) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            }
        }
    });

    document.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        const touch = e.touches?.[0];
        if (!touch) return;

        const cropBox = handle.parentElement;
        const imageContainer = cropBox.parentElement;
        const imageElement = imageContainer.querySelector('.image-preview, .preview-image');

        const metrics = getImageDisplayMetrics(imageContainer, img);
        if (!metrics) return;
        const { scale } = metrics;

        const dx = (touch.clientX - startX) / scale;
        const dy = (touch.clientY - startY) / scale;

        let newCrop = { ...startCrop };

        // Adjust based on handle position
        if (position.includes('n')) {
            newCrop.y = startCrop.y + dy;
            newCrop.height = startCrop.height - dy;
        }
        if (position.includes('s')) {
            newCrop.height = startCrop.height + dy;
        }
        if (position.includes('w')) {
            newCrop.x = startCrop.x + dx;
            newCrop.width = startCrop.width - dx;
        }
        if (position.includes('e')) {
            newCrop.width = startCrop.width + dx;
        }

        // Maintain aspect ratio
        if (isAspectLockedMode()) {
            newCrop = constrainCropWithAspect(newCrop, startCrop, position, img, currentAspectRatio);
        }

        // Constrain to image bounds
        newCrop = clampCropToBounds(newCrop, img);

        // Minimum size
        if (newCrop.width > 20 && newCrop.height > 20) {
            img.crop = newCrop;
            if (
                newCrop.x !== startCrop.x ||
                newCrop.y !== startCrop.y ||
                newCrop.width !== startCrop.width ||
                newCrop.height !== startCrop.height
            ) {
                didChange = true;
                lastChangeId = ++cropChangeId;
                img._lastCropChangeId = lastChangeId;
            }
            updateCropBoxStyle(cropBox, img, isPreview);

            if (isPreview) {
                updateGridCropIfVisible(img);
                if (didChange) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            } else {
                if (selectedImageId === img.id) {
                    updatePreviewCropIfVisible(img);
                }
                if (didChange && selectedImageId === img.id) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsDrag(), lastChangeId);
                }
            }
        }

        e.preventDefault();
    }, { passive: false });

    document.addEventListener('mouseup', () => {
        if (isDragging) {
            isDragging = false;
            activePointerId = null;
            activeInput = null;
            handle.classList.remove('dragging');
            if (!isPreview) {
                updatePreviewCropIfVisible(img);
                if (didChange && selectedImageId === img.id) {
                    if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                        scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                    }
                }
            } else {
                updateGridCropIfVisible(img);
                if (didChange) {
                    if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                        scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                    }
                }
            }
        }
    });

    document.addEventListener('pointerup', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        if (activeInput && activeInput !== 'pointer') return;

        isDragging = false;
        activePointerId = null;
        activeInput = null;
        handle.classList.remove('dragging');
        if (!isPreview) {
            updatePreviewCropIfVisible(img);
            if (didChange && selectedImageId === img.id) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        } else {
            updateGridCropIfVisible(img);
            if (didChange) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        }
    });

    document.addEventListener('pointercancel', (e) => {
        if (e.pointerType === 'mouse') return;
        if (!isDragging) return;
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
        handle.classList.remove('dragging');
    });

    document.addEventListener('touchend', () => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
        handle.classList.remove('dragging');
        if (!isPreview) {
            updatePreviewCropIfVisible(img);
            if (didChange && selectedImageId === img.id) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        } else {
            updateGridCropIfVisible(img);
            if (didChange) {
                if ((img._lastCropChangeId || 0) > exportPreviewLastScheduledChangeId) {
                    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), img._lastCropChangeId || 0);
                }
            }
        }
    });

    document.addEventListener('touchcancel', () => {
        if (!isDragging) return;
        if (activeInput !== 'touch') return;
        isDragging = false;
        activePointerId = null;
        activeInput = null;
        handle.classList.remove('dragging');
    });
}

function updatePreviewCropIfVisible(img) {
    if (!selectedImageId || selectedImageId !== img.id) return;
    const previewWrapper = document.getElementById('previewWrapper');
    if (!previewWrapper) return;
    const cropBox = previewWrapper.querySelector('.crop-box');
    if (cropBox) {
        updateCropBoxStyle(cropBox, img, true);
    }
}

function updateGridCropIfVisible(img) {
    const grid = document.getElementById('imagesGrid');
    if (!grid) return;
    const card = grid.querySelector(`[data-image-id="${img.id}"]`);
    if (!card) return;
    const cropBox = card.querySelector('.crop-box');
    if (cropBox) {
        updateCropBoxStyle(cropBox, img, false);
    }
}

function clampCropToBounds(crop, img) {
    const clamped = { ...crop };
    if (clamped.x < 0) {
        clamped.width += clamped.x;
        clamped.x = 0;
    }
    if (clamped.y < 0) {
        clamped.height += clamped.y;
        clamped.y = 0;
    }
    if (clamped.x + clamped.width > img.width) {
        clamped.width = img.width - clamped.x;
    }
    if (clamped.y + clamped.height > img.height) {
        clamped.height = img.height - clamped.y;
    }
    return clamped;
}

function clampCropToBoundsWithAspect(crop, img, aspect) {
    const clamped = { ...crop };
    const maxWidth = img.width;
    const maxHeight = img.height;
    if (clamped.width > maxWidth) {
        clamped.width = maxWidth;
        clamped.height = clamped.width / aspect;
    }
    if (clamped.height > maxHeight) {
        clamped.height = maxHeight;
        clamped.width = clamped.height * aspect;
    }
    if (clamped.width > maxWidth) {
        clamped.width = maxWidth;
        clamped.height = clamped.width / aspect;
    }
    clamped.x = Math.max(0, Math.min(clamped.x, img.width - clamped.width));
    clamped.y = Math.max(0, Math.min(clamped.y, img.height - clamped.height));
    return clamped;
}

function applyCropUpdate(img, newCrop) {
    if (!img || !newCrop) return;
    img.crop = newCrop;
    const lastChangeId = ++cropChangeId;
    img._lastCropChangeId = lastChangeId;
    updatePreviewCropIfVisible(img);
    updateGridCropIfVisible(img);
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMsMouseup(), lastChangeId);
}

function resizeCropFromCenter(img, direction, step, aspect) {
    const crop = img.crop || { x: 0, y: 0, width: img.width, height: img.height };
    const minSize = 20;
    const cx = crop.x + crop.width / 2;
    const cy = crop.y + crop.height / 2;
    let newWidth = crop.width + (direction * 2 * step);
    let newHeight = crop.height + (direction * 2 * step);

    if (aspect) {
        if (aspect >= 1) {
            const minWidth = Math.max(minSize, minSize * aspect);
            newWidth = Math.max(minWidth, newWidth);
            newHeight = newWidth / aspect;
        } else {
            const minHeight = Math.max(minSize, minSize / aspect);
            newHeight = Math.max(minHeight, newHeight);
            newWidth = newHeight * aspect;
        }
    } else {
        newWidth = Math.max(minSize, newWidth);
        newHeight = Math.max(minSize, newHeight);
    }

    let next = {
        x: cx - newWidth / 2,
        y: cy - newHeight / 2,
        width: newWidth,
        height: newHeight
    };

    next = aspect
        ? clampCropToBoundsWithAspect(next, img, aspect)
        : clampCropToBounds(next, img);

    return next;
}

function nudgeCrop(action, step) {
    if (!selectedImageId) return;
    if (!shouldShowCropOverlay()) return;
    const img = images.find(i => i.id === selectedImageId);
    if (!img || !img.crop) return;
    const aspect = isAspectLockedMode() ? currentAspectRatio : null;
    const crop = { ...img.crop };
    let next = { ...crop };

    switch (action) {
        case 'up':
            next.y -= step;
            next = clampCropToBounds(next, img);
            break;
        case 'down':
            next.y += step;
            next = clampCropToBounds(next, img);
            break;
        case 'left':
            next.x -= step;
            next = clampCropToBounds(next, img);
            break;
        case 'right':
            next.x += step;
            next = clampCropToBounds(next, img);
            break;
        case 'grow':
            next = resizeCropFromCenter(img, 1, step, aspect);
            break;
        case 'shrink':
            next = resizeCropFromCenter(img, -1, step, aspect);
            break;
        default:
            return;
    }

    applyCropUpdate(img, next);
}

function updatePreviewNudgeControls() {
    const footer = document.getElementById('previewFooter');
    if (!footer) return;
    const previewPane = document.getElementById('previewPane');
    const canShow = Boolean(
        previewPane?.classList.contains('active')
        && selectedImageId
        && shouldShowCropOverlay()
    );
    footer.classList.toggle('hidden', !canShow);
}

function constrainCropWithAspect(crop, startCrop, position, img, aspect) {
    const result = { ...crop };

    const anchorLeft = position.includes('w');
    const anchorTop = position.includes('n');
    const anchorRight = position.includes('e');
    const anchorBottom = position.includes('s');

    if (anchorLeft) {
        result.width = startCrop.x + startCrop.width - result.x;
    }
    if (anchorTop) {
        result.height = startCrop.y + startCrop.height - result.y;
    }

    if (anchorRight || anchorLeft) {
        result.height = result.width / aspect;
        if (anchorTop) {
            result.y = startCrop.y + startCrop.height - result.height;
        }
    } else if (anchorBottom || anchorTop) {
        result.width = result.height * aspect;
        if (anchorLeft) {
            result.x = startCrop.x + startCrop.width - result.width;
        }
    }

    // Fit within bounds while keeping aspect
    if (result.x < 0) {
        result.x = 0;
        result.width = startCrop.x + startCrop.width;
        result.height = result.width / aspect;
    }
    if (result.y < 0) {
        result.y = 0;
        result.height = startCrop.y + startCrop.height;
        result.width = result.height * aspect;
    }
    if (result.x + result.width > img.width) {
        result.width = img.width - result.x;
        result.height = result.width / aspect;
        if (anchorTop) {
            result.y = startCrop.y + startCrop.height - result.height;
        }
    }
    if (result.y + result.height > img.height) {
        result.height = img.height - result.y;
        result.width = result.height * aspect;
        if (anchorLeft) {
            result.x = startCrop.x + startCrop.width - result.width;
        }
    }

    return result;
}

function showPreview(id) {
    selectedImageId = id;
    const img = images.find(i => i.id === id);
    const previewPane = document.getElementById('previewPane');
    const previewWrapper = document.getElementById('previewWrapper');
    const previewTitle = document.getElementById('previewTitle');
    const exportOverlay = document.querySelector('.export-preview-overlay');

    if (!img) {
        forceResetPreviewState();
        return;
    }
    if (!previewPane || !previewWrapper || !previewTitle) {
        return;
    }
    if (exportPreviewDebounceTimer) {
        clearTimeout(exportPreviewDebounceTimer);
        exportPreviewDebounceTimer = null;
    }

    previewPane.classList.add('active');
    document.body.classList.add('preview-active');
    updateMainResizerState();
    updatePreviewFullscreenButton();
    previewTitle.dataset.baseTitle = img.name;
    previewTitle.textContent = img.name;

    previewWrapper.innerHTML = '';

    const imageContainer = document.createElement('div');
    imageContainer.style.position = 'relative';
    imageContainer.style.display = 'block';
    imageContainer.style.lineHeight = '0';
    imageContainer.style.width = '100%';
    imageContainer.style.height = '100%';

    const image = document.createElement('img');
    image.className = 'preview-image';
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'contain';
    image.style.position = 'relative';

    imageContainer.appendChild(image);
    previewWrapper.appendChild(imageContainer);

    // Wait for image to load and render
    image.onload = () => {
        attachCropBox(imageContainer, image, img, true);
        requestAnimationFrame(() => {
            const cropBox = imageContainer.querySelector('.crop-box');
            if (cropBox) {
                updateCropBoxStyle(cropBox, img, true);
            }
            updatePreviewZoomLabels();
        });
    };
    image.src = img.src;

    if (image.complete && image.naturalWidth > 0) {
        requestAnimationFrame(() => {
            attachCropBox(imageContainer, image, img, true);
        });
    }

    updateSelectedCard();
    updatePreviewNudgeControls();
    const forceFullscreen = window.innerWidth < PREVIEW_FULLSCREEN_BREAKPOINT;
    forcedFullscreenPreview = forceFullscreen;
    if (forceFullscreen) {
        if (isSplitPreview) {
            setSplitPreview(false, true);
        }
        if (!isPreviewPaneFullscreen()) {
            togglePreviewFullscreen();
        }
    }
    const wantsSplit = lastExportPreviewMode === 'split'
        || lastExportPreviewMode === 'split-horizontal'
        || lastExportPreviewMode === 'split-vertical';
    if (wantsSplit && !forceFullscreen) {
        const orientation = lastExportPreviewMode === 'split-vertical' ? 'vertical' : 'horizontal';
        if (!isSplitPreview || splitOrientation !== orientation) {
            setSplitPreview(true, true, orientation);
            return;
        }
        openExportPreview(img, { preserveImage: true });
        return;
    } else if (lastExportPreviewMode === 'full') {
        if (isSplitPreview) {
            setSplitPreview(false, true);
        }
        openExportPreview(img, { preserveImage: true });
    } else if (isSplitPreview) {
        openExportPreview(img, { preserveImage: true });
    } else if (exportOverlay) {
        openExportPreview(img, { preserveImage: true });
    }
}

function finalizeClosePreview(skipSplitReset = false) {
    document.getElementById('previewPane').classList.remove('active');
    document.body.classList.remove('preview-active');
    updateMainResizerState();
    selectedImageId = null;
    forcedFullscreenPreview = false;
    previewFullscreen = false;
    document.body.classList.remove('preview-fullscreen');
    updatePreviewFullscreenButton();
    exportPreviewRequestId += 1;
    if (exportPreviewDebounceTimer) {
        clearTimeout(exportPreviewDebounceTimer);
        exportPreviewDebounceTimer = null;
    }
    isExportPreviewVisible = false;
    updateTestExportButton();
    const wasSplit = isSplitPreview;
    if (!skipSplitReset) {
        if (wasSplit) {
            setSplitPreview(false);
            lastExportPreviewMode = splitOrientation === 'vertical' ? 'split-vertical' : 'split-horizontal';
        } else {
            isSplitPreview = false;
            const previewContent = document.getElementById('previewContent');
            if (previewContent) {
                previewContent.classList.remove('split-active');
                previewContent.classList.remove('split-vertical');
            }
            updateTestExportButton();
            updatePreviewLayoutButtons();
            lastExportPreviewMode = 'none';
        }
    }
    if (wasSplit) {
        const host = document.getElementById('exportPreviewHost');
        if (host) {
            host.innerHTML = '';
        }
    }
    // Defensive cleanup to avoid leaving split layout artifacts.
    isSplitPreview = false;
    if (previewLeft) {
        previewLeft.style.flex = '';
    }
    if (imagesArea) {
        imagesArea.style.flex = '';
    }
    const previewContent = document.getElementById('previewContent');
    if (previewContent) {
        previewContent.classList.remove('split-active');
        previewContent.classList.remove('split-vertical');
    }
    updateSelectedCard();
    updatePreviewNudgeControls();
}

function closePreview(skipSplitReset = false) {
    const previewPaneEl = document.getElementById('previewPane');
    if (document.fullscreenElement && document.fullscreenElement === previewPaneEl) {
        pendingClosePreviewAfterFullscreen = { skipSplitReset };
        document.exitFullscreen().catch(() => {
            const pending = pendingClosePreviewAfterFullscreen;
            pendingClosePreviewAfterFullscreen = null;
            finalizeClosePreview(Boolean(pending?.skipSplitReset));
        });
        return;
    }
    finalizeClosePreview(skipSplitReset);
}

function forceResetPreviewState() {
    const previewPane = document.getElementById('previewPane');
    const previewContent = document.getElementById('previewContent');
    const previewWrapper = document.getElementById('previewWrapper');
    const exportHost = document.getElementById('exportPreviewHost');
    const overlay = document.querySelector('.export-preview-overlay');
    if (overlay) overlay.remove();
    if (exportHost) exportHost.innerHTML = '';
    if (previewWrapper) previewWrapper.innerHTML = '';
    if (previewPane) previewPane.classList.remove('active');
    document.body.classList.remove('preview-active');
    if (previewContent) {
        previewContent.classList.remove('split-active');
        previewContent.classList.remove('split-vertical');
    }
    if (previewLeft) {
        previewLeft.style.flex = '';
    }
    if (imagesArea) {
        imagesArea.style.flex = '';
    }
    isSplitPreview = false;
    isExportPreviewVisible = false;
    lastExportPreviewMode = 'none';
    selectedImageId = null;
    forcedFullscreenPreview = false;
    updateMainResizerState();
    updateTestExportButton();
    updatePreviewLayoutButtons();
    updatePreviewNudgeControls();
    persistPreferences({ lastExportPreviewMode: 'none' });
}

function testExport() {
    if (!selectedImageId) return;
    if (isExportPreviewVisible && !isSplitPreview) {
        closeExportPreview();
        lastExportPreviewMode = 'none';
        updateTestExportButton();
        return;
    }

    const img = images.find(i => i.id === selectedImageId);
    openExportPreview(img);
    lastExportPreviewMode = 'full';
    updateTestExportButton();
}

function closeExportPreview(preserveMode = false) {
    const overlay = document.querySelector('.export-preview-overlay');
    if (overlay) overlay.remove();
    ensureExportPreviewPlaceholder();
    isExportPreviewVisible = false;
    updateTestExportButton();
    if (!preserveMode) {
        lastExportPreviewMode = 'none';
    }
    if (!isSplitPreview && selectedImageId) {
        const img = images.find(i => i.id === selectedImageId);
        if (img) {
            showPreview(selectedImageId);
        }
    }
}

function getExportPreviewContainer() {
    const previewContent = document.getElementById('previewContent');
    const exportHost = document.getElementById('exportPreviewHost');
    if (isSplitPreview && exportHost) {
        return exportHost;
    }
    return previewContent;
}

function ensureExportPreviewPlaceholder() {
    if (!isSplitPreview) return;
    const host = document.getElementById('exportPreviewHost');
    if (!host) return;
    if (host.querySelector('.export-preview-overlay')) return;
    host.innerHTML = '<div class="export-preview-placeholder">Test Export to compare</div>';
}

function updateExportPreviewSizeFromImage(overlay, exportImg, sizeBytes, expectedWidth, expectedHeight) {
    if (!overlay || !exportImg) return;
    const sizeEl = overlay.querySelector('.export-preview-size');
    if (!sizeEl) return;
    const actualWidth = exportImg.naturalWidth;
    const actualHeight = exportImg.naturalHeight;
    const parts = [];
    if (Number.isFinite(sizeBytes) && sizeBytes >= 0) {
        parts.push(`${(sizeBytes / 1024).toFixed(1)} KB`);
    }
    if (Number.isFinite(actualWidth) && Number.isFinite(actualHeight) && actualWidth > 0 && actualHeight > 0) {
        parts.push(`${actualWidth}x${actualHeight}`);
    }
    const hasExpected = Number.isFinite(expectedWidth)
        && Number.isFinite(expectedHeight)
        && expectedWidth > 0
        && expectedHeight > 0;
    const mismatch = hasExpected
        && Number.isFinite(actualWidth)
        && Number.isFinite(actualHeight)
        && actualWidth > 0
        && actualHeight > 0
        && (expectedWidth !== actualWidth || expectedHeight !== actualHeight);
    if (mismatch) {
        parts.push(`expected ${expectedWidth}x${expectedHeight}`);
        console.warn('Export preview size mismatch', {
            expected: { width: expectedWidth, height: expectedHeight },
            actual: { width: actualWidth, height: actualHeight }
        });
    }
    sizeEl.style.color = mismatch ? 'var(--warning)' : 'var(--text-secondary)';
    if (parts.length) {
        const label = mismatch ? `MISMATCH: ${parts.join(' · ')}` : parts.join(' · ');
        sizeEl.textContent = `(${label})`;
    } else {
        sizeEl.textContent = '';
    }
}

function getContentInnerSize(el) {
    if (!el) return { width: 0, height: 0 };
    const style = window.getComputedStyle(el);
    const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const width = el.clientWidth - (Number.isFinite(paddingX) ? paddingX : 0);
    const height = el.clientHeight - (Number.isFinite(paddingY) ? paddingY : 0);
    return {
        width: Math.max(0, width),
        height: Math.max(0, height)
    };
}

function getExportPreviewFitScale(scrollEl, exportImg) {
    if (!scrollEl || !exportImg || !exportImg.naturalWidth || !exportImg.naturalHeight) return 1;
    const inner = getContentInnerSize(scrollEl);
    if (inner.width <= 0 || inner.height <= 0) return 1;
    const scale = Math.min(inner.width / exportImg.naturalWidth, inner.height / exportImg.naturalHeight, 1);
    return Number.isFinite(scale) ? scale : 1;
}

function setExportPreviewZoomMode(overlay, mode, options = {}) {
    if (!overlay) return;
    const scrollEl = overlay.querySelector('.export-preview-scroll');
    if (!scrollEl) return;
    const normalized = ['fit', '100', '200', '400'].includes(mode) ? mode : 'fit';
    overlay.dataset.zoomMode = normalized;
    scrollEl.classList.toggle('zoom-actual', normalized !== 'fit');
    const controls = overlay.querySelector('.export-preview-zoom-controls');
    if (controls) {
        controls.querySelectorAll('button[data-zoom]').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.zoom === normalized);
        });
    }
    if (normalized === 'fit' && options.resetScroll !== false) {
        scrollEl.scrollLeft = 0;
        scrollEl.scrollTop = 0;
    }
}

function applyExportPreviewZoom(overlay, exportImg) {
    if (!overlay || !exportImg || !exportImg.naturalWidth || !exportImg.naturalHeight) return;
    const mode = overlay.dataset.zoomMode || 'fit';
    if (mode === 'fit') {
        exportImg.style.width = '';
        exportImg.style.height = '';
        return;
    }
    let scale = 1;
    if (mode === '200') scale = 2;
    if (mode === '400') scale = 4;
    exportImg.style.width = `${Math.max(1, Math.round(exportImg.naturalWidth * scale))}px`;
    exportImg.style.height = `${Math.max(1, Math.round(exportImg.naturalHeight * scale))}px`;
}

function getExportPreviewScale(overlay, exportImg) {
    if (!overlay || !exportImg) return 1;
    const mode = overlay.dataset.zoomMode || 'fit';
    if (mode === 'fit') {
        return getExportPreviewFitScale(overlay.querySelector('.export-preview-scroll'), exportImg);
    }
    if (mode === '200') return 2;
    if (mode === '400') return 4;
    return 1;
}

function updateExportPreviewZoomUI(overlay, exportImg) {
    if (!overlay) return;
    const scrollEl = overlay.querySelector('.export-preview-scroll');
    const controls = overlay.querySelector('.export-preview-zoom-controls');
    if (!scrollEl || !controls) return;
    controls.style.display = 'inline-flex';
    setExportPreviewZoomMode(overlay, overlay.dataset.zoomMode || 'fit', { resetScroll: false });
    applyExportPreviewZoom(overlay, exportImg);
}

function bindExportPreviewPanHandlers(content) {
    if (!content || content.dataset.panBound) return;
    content.dataset.panBound = 'true';
    let isPanning = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    content.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        isPanning = true;
        startX = e.clientX;
        startY = e.clientY;
        startLeft = content.scrollLeft;
        startTop = content.scrollTop;
        content.classList.add('panning');
        content.setPointerCapture(e.pointerId);
        e.preventDefault();
    });

    content.addEventListener('pointermove', (e) => {
        if (!isPanning) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        content.scrollLeft = startLeft - dx;
        content.scrollTop = startTop - dy;
    });

    const endPan = (e) => {
        if (!isPanning) return;
        isPanning = false;
        content.classList.remove('panning');
        if (e && content.hasPointerCapture?.(e.pointerId)) {
            content.releasePointerCapture(e.pointerId);
        }
    };

    content.addEventListener('pointerup', endPan);
    content.addEventListener('pointercancel', endPan);
}

function bindExportPreviewZoomControls(overlay) {
    if (!overlay || overlay.dataset.zoomBound) return;
    overlay.dataset.zoomBound = 'true';
    const controls = overlay.querySelector('.export-preview-zoom-controls');
    if (!controls) return;
    controls.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-zoom]');
        if (!btn) return;
        const scrollEl = overlay.querySelector('.export-preview-scroll');
        const exportImg = overlay.querySelector('.export-preview-image');
        let centerX = null;
        let centerY = null;
        if (scrollEl && exportImg) {
            const prevScale = getExportPreviewScale(overlay, exportImg) || 1;
            if (prevScale > 0) {
                centerX = (scrollEl.scrollLeft + scrollEl.clientWidth / 2) / prevScale;
                centerY = (scrollEl.scrollTop + scrollEl.clientHeight / 2) / prevScale;
            }
        }
        setExportPreviewZoomMode(overlay, btn.dataset.zoom, { resetScroll: false });
        updateExportPreviewZoomUI(overlay, exportImg);
        if (scrollEl && exportImg && centerX !== null && centerY !== null) {
            requestAnimationFrame(() => {
                const nextScale = getExportPreviewScale(overlay, exportImg) || 1;
                const targetLeft = centerX * nextScale - scrollEl.clientWidth / 2;
                const targetTop = centerY * nextScale - scrollEl.clientHeight / 2;
                const maxLeft = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
                const maxTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
                scrollEl.scrollLeft = Math.min(maxLeft, Math.max(0, targetLeft));
                scrollEl.scrollTop = Math.min(maxTop, Math.max(0, targetTop));
            });
        }
        updatePreviewZoomLabels();
    });
}

function openExportPreview(img, options = {}) {
    const { preserveImage = false } = options;
    const previewContent = document.getElementById('previewContent');
    const exportHost = document.getElementById('exportPreviewHost');
    const container = getExportPreviewContainer();
    let overlay = container ? container.querySelector('.export-preview-overlay') : null;
    let content;
    const isSingleView = !isSplitPreview && container === previewContent;
    const requestId = ++exportPreviewRequestId;
    if (!overlay && exportHost && previewContent) {
        const other = container === previewContent ? exportHost : previewContent;
        const existingOther = other.querySelector('.export-preview-overlay');
        if (existingOther) {
            existingOther.remove();
        }
    }
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'export-preview-overlay';

        const header = document.createElement('div');
        header.className = 'export-preview-header';
        const titleRow = document.createElement('div');
        titleRow.className = 'export-preview-title-row';
        const titleSpan = document.createElement('span');
        titleSpan.className = 'export-preview-title';
        titleSpan.dataset.baseTitle = 'Export Preview';
        titleSpan.textContent = 'Export Preview';
        const sizeSpan = document.createElement('span');
        sizeSpan.className = 'export-preview-size';
        titleRow.appendChild(titleSpan);
        titleRow.appendChild(sizeSpan);

        const headerActions = document.createElement('div');
        headerActions.className = 'export-preview-header-actions';

        const zoomControls = document.createElement('div');
        zoomControls.className = 'export-preview-zoom-controls';
        zoomControls.style.display = 'none';
        const fitBtn = document.createElement('button');
        fitBtn.className = 'btn export-zoom-btn';
        fitBtn.dataset.zoom = 'fit';
        fitBtn.textContent = 'Fit';
        const fullBtn = document.createElement('button');
        fullBtn.className = 'btn export-zoom-btn';
        fullBtn.dataset.zoom = '100';
        fullBtn.textContent = '100%';
        const doubleBtn = document.createElement('button');
        doubleBtn.className = 'btn export-zoom-btn';
        doubleBtn.dataset.zoom = '200';
        doubleBtn.textContent = '200%';
        const quadBtn = document.createElement('button');
        quadBtn.className = 'btn export-zoom-btn';
        quadBtn.dataset.zoom = '400';
        quadBtn.textContent = '400%';
        zoomControls.appendChild(fitBtn);
        zoomControls.appendChild(fullBtn);
        zoomControls.appendChild(doubleBtn);
        zoomControls.appendChild(quadBtn);
        headerActions.appendChild(zoomControls);

        if (!isSplitPreview) {
            const closeBtn = document.createElement('button');
            closeBtn.className = 'icon-btn';
            closeBtn.textContent = '✕';
            closeBtn.type = 'button';
            closeBtn.addEventListener('pointerdown', (e) => {
                e.stopPropagation();
            });
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeExportPreview();
                updateTestExportButton();
            });
            headerActions.appendChild(closeBtn);
        }

        header.appendChild(titleRow);
        header.appendChild(headerActions);

        content = document.createElement('div');
        content.className = 'export-preview-content';
        const scroll = document.createElement('div');
        scroll.className = 'export-preview-scroll';
        content.appendChild(scroll);

        overlay.appendChild(header);
        overlay.appendChild(content);
        if (container) {
            if (isSingleView) {
                container.appendChild(overlay);
            } else {
                container.innerHTML = '';
                container.appendChild(overlay);
            }
        }
        isExportPreviewVisible = true;
        updateTestExportButton();
        bindExportPreviewPanHandlers(scroll);
        bindExportPreviewZoomControls(overlay);
        setExportPreviewZoomMode(overlay, 'fit');
    } else {
        content = overlay.querySelector('.export-preview-content');
        isExportPreviewVisible = true;
        updateTestExportButton();
        let scroll = overlay.querySelector('.export-preview-scroll');
        if (!scroll && content) {
            scroll = document.createElement('div');
            scroll.className = 'export-preview-scroll';
            scroll.innerHTML = content.innerHTML;
            content.innerHTML = '';
            content.appendChild(scroll);
        }
        bindExportPreviewPanHandlers(scroll);
        bindExportPreviewZoomControls(overlay);
    }

    if (canReuseCachedExportPreview(img)) {
        const scroll = overlay?.querySelector('.export-preview-scroll');
        if (scroll) {
            scroll.innerHTML = '';
            const exportImg = document.createElement('img');
            exportImg.src = lastExportPreview.blobUrl;
            exportImg.className = 'export-preview-image';
            exportImg.dataset.blobUrl = lastExportPreview.blobUrl;
            exportImg.draggable = false;
            exportImg.onload = () => {
                overlay.classList.remove('loading');
                const { width, height } = getExportOutputDimensions(img);
                updateExportPreviewSizeFromImage(overlay, exportImg, lastExportPreview.sizeBytes, width, height);
                updateExportPreviewZoomUI(overlay, exportImg);
                updatePreviewZoomLabels();
            };
            scroll.appendChild(exportImg);
        }
        return;
    }

    const scroll = overlay?.querySelector('.export-preview-scroll');
    const existingImage = scroll ? scroll.querySelector('.export-preview-image') : null;
    const sameImage = lastExportPreview && lastExportPreview.imageId === img.id;
    if (overlay) {
        overlay.classList.remove('loading');
        overlay.dataset.requestId = String(requestId);
    }
    if (overlay) {
        const sizeEl = overlay.querySelector('.export-preview-size');
        if (sizeEl) {
            sizeEl.textContent = '';
            sizeEl.style.color = 'var(--text-secondary)';
        }
    }
    if (existingImage && preserveImage && sameImage) {
        overlay.classList.add('loading');
    } else if (scroll) {
        scroll.innerHTML = '<div style="color: var(--text-secondary);">Processing...</div>';
    }

    // Process image
    const processingSnapshot = createProcessingSnapshot(img);
    processImage(img, { cancelOnChange: true, snapshot: processingSnapshot, yieldMs: 1 }).then(blob => {
        if (!overlay || overlay.dataset.requestId !== String(requestId)) {
            return;
        }
        const blobUrl = URL.createObjectURL(blob);
        const { width, height } = getExportOutputDimensions(img);
        lastExportPreview = {
            imageId: img.id,
            cropChangeId: img._lastCropChangeId || 0,
            settingsChangeId: exportSettingsChangeId,
            blobUrl,
            sizeBytes: blob.size,
            width,
            height
        };
        if (existingImage && preserveImage && sameImage) {
            const previousUrl = existingImage.dataset.blobUrl;
            if (previousUrl) {
                URL.revokeObjectURL(previousUrl);
            }
            existingImage.onload = () => {
                if (!overlay || overlay.dataset.requestId !== String(requestId)) {
                    return;
                }
                overlay.classList.remove('loading');
                updateExportPreviewSizeFromImage(overlay, existingImage, blob.size, width, height);
                updateExportPreviewZoomUI(overlay, existingImage);
                updatePreviewZoomLabels();
            };
            existingImage.draggable = false;
            existingImage.src = blobUrl;
            existingImage.dataset.blobUrl = blobUrl;
        } else {
            const exportImg = document.createElement('img');
            exportImg.src = blobUrl;
            exportImg.className = 'export-preview-image';
            exportImg.dataset.blobUrl = blobUrl;
            exportImg.draggable = false;
            exportImg.onload = () => {
                if (!overlay || overlay.dataset.requestId !== String(requestId)) {
                    return;
                }
                if (scroll) {
                    scroll.innerHTML = '';
                    scroll.appendChild(exportImg);
                }
                overlay.classList.remove('loading');
                updateExportPreviewSizeFromImage(overlay, exportImg, blob.size, width, height);
                updateExportPreviewZoomUI(overlay, exportImg);
                updatePreviewZoomLabels();
            };
        }
    }).catch(err => {
        if (!overlay || overlay.dataset.requestId !== String(requestId)) {
            return;
        }
        if (err && (err.code === 'INTERRUPTED' || err.message === 'Interrupted')) {
            if (scroll) {
                scroll.innerHTML = '<div style="color: var(--warning);">Interrupted</div>';
            }
            overlay.classList.remove('loading');
            return;
        }
        if (scroll) {
            scroll.innerHTML = `<div style="color: var(--danger);">Error: ${err.message}</div>`;
        }
        overlay.classList.remove('loading');
    });
}

function refreshExportPreviewIfVisible(options = {}) {
    if (!isExportPreviewVisible || !selectedImageId) return;
    const overlay = document.querySelector('.export-preview-overlay');
    if (!overlay) return;
    const img = images.find(i => i.id === selectedImageId);
    if (!img) return;
    openExportPreview(img, options);
}

function scheduleExportPreviewRefresh(options = {}, delay = 180, changeId = null) {
    if (delay === null) {
        return;
    }
    if (changeId !== null) {
        exportPreviewLastScheduledChangeId = changeId;
    }
    if (exportPreviewDebounceTimer) {
        clearTimeout(exportPreviewDebounceTimer);
    }
    exportPreviewDebounceTimer = setTimeout(() => {
        exportPreviewDebounceTimer = null;
        if (changeId !== null) {
            exportPreviewLastRenderedChangeId = changeId;
        }
        refreshExportPreviewIfVisible(options);
    }, delay);
}

function bumpExportSettingsChange() {
    exportSettingsChangeId += 1;
    if (showGridSizeInfo) {
        updateGridInfoLabels();
    }
}

function canReuseCachedExportPreview(img) {
    if (!lastExportPreview.blobUrl) return false;
    if (lastExportPreview.imageId !== img.id) return false;
    const currentCropId = img._lastCropChangeId || 0;
    if (lastExportPreview.cropChangeId !== currentCropId) return false;
    if (lastExportPreview.settingsChangeId !== exportSettingsChangeId) return false;
    return true;
}

function computeScalePercent(imgEl) {
    if (!imgEl || !imgEl.naturalWidth || !imgEl.naturalHeight) return null;
    const rect = imgEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scale = Math.min(rect.width / imgEl.naturalWidth, rect.height / imgEl.naturalHeight);
    return Math.round(scale * 100);
}

function updateTitleWithZoom(titleEl, imgEl) {
    if (!titleEl) return;
    const baseTitle = titleEl.dataset.baseTitle || titleEl.textContent;
    const percent = computeScalePercent(imgEl);
    const titleTextEl = titleEl.querySelector('.preview-title-text');
    const titleZoomEl = titleEl.querySelector('.preview-title-zoom');
    if (titleTextEl && titleZoomEl) {
        titleTextEl.textContent = baseTitle;
        if (percent && percent < 100) {
            titleZoomEl.textContent = `[${percent}%]`;
        } else {
            titleZoomEl.textContent = '';
        }
        return;
    }
    if (percent && percent < 100) {
        titleEl.textContent = `${baseTitle} [${percent}%]`;
    } else {
        titleEl.textContent = baseTitle;
    }
}

function updatePreviewZoomLabels() {
    const previewTitle = document.getElementById('previewTitle');
    const previewImage = document.querySelector('#previewWrapper .preview-image');
    updateTitleWithZoom(previewTitle, previewImage);

    const exportTitle = document.querySelector('.export-preview-title');
    const exportImage = document.querySelector('.export-preview-image');
    updateTitleWithZoom(exportTitle, exportImage);
    const overlay = document.querySelector('.export-preview-overlay');
    if (overlay && exportImage) {
        updateExportPreviewZoomUI(overlay, exportImage);
    }
}

function resizeWithCanvas(sourceCanvas, width, height) {
    const destCanvas = document.createElement('canvas');
    destCanvas.width = width;
    destCanvas.height = height;
    const destCtx = destCanvas.getContext('2d', { alpha: true });
    destCtx.imageSmoothingEnabled = true;
    destCtx.imageSmoothingQuality = 'high';
    destCtx.drawImage(sourceCanvas, 0, 0, width, height);
    return destCtx.getImageData(0, 0, width, height);
}

async function ensureOxipngReady() {
    if (!oxipngReady) {
        oxipngReady = initOxipng();
    }
    await oxipngReady;
}

function encodeWithCanvasFallback(imageData, format) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        const ctx = canvas.getContext('2d', { alpha: true });
        ctx.putImageData(imageData, 0, 0);

        let quality;
        let mimeType;
        switch (format) {
            case 'jpeg':
                mimeType = 'image/jpeg';
                quality = parseInt(document.getElementById('jpegQuality').value) / 100;
                break;
            case 'webp':
                mimeType = 'image/webp';
                quality = parseInt(document.getElementById('webpQuality').value) / 100;
                break;
            case 'avif':
                mimeType = 'image/avif';
                quality = parseInt(document.getElementById('avifQuality').value) / 100;
                break;
            case 'png':
                mimeType = 'image/png';
                quality = 1;
                break;
            case 'gif':
                mimeType = 'image/png';
                quality = 1;
                break;
            default:
                mimeType = 'image/png';
                quality = 1;
        }

        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to create blob'));
            }
        }, mimeType, quality);
    });
}

async function encodeWithJsquash(imageData, format) {
    switch (format) {
        case 'jpeg':
            return {
                mimeType: 'image/jpeg',
                data: await encodeJpeg(imageData, {
                    quality: parseInt(document.getElementById('jpegQuality').value)
                })
            };
        case 'webp':
            return {
                mimeType: 'image/webp',
                data: await encodeWebp(imageData, {
                    quality: parseInt(document.getElementById('webpQuality').value)
                })
            };
        case 'avif':
            return {
                mimeType: 'image/avif',
                data: await encodeAvif(imageData, {
                    quality: parseInt(document.getElementById('avifQuality').value)
                })
            };
        case 'png':
            await ensureOxipngReady();
            {
                const data = optimiseOxiPngRaw(
                    imageData.data,
                    imageData.width,
                    imageData.height,
                    OXIPNG_DEFAULTS.level,
                    OXIPNG_DEFAULTS.interlace,
                    OXIPNG_DEFAULTS.optimiseAlpha
                );
                return {
                    mimeType: 'image/png',
                    data: data instanceof Uint8Array ? data : new Uint8Array(data)
                };
            }
        case 'gif':
            await ensureOxipngReady();
            {
                const data = optimiseOxiPngRaw(
                    imageData.data,
                    imageData.width,
                    imageData.height,
                    OXIPNG_DEFAULTS.level,
                    OXIPNG_DEFAULTS.interlace,
                    OXIPNG_DEFAULTS.optimiseAlpha
                );
                return {
                    mimeType: 'image/png',
                    data: data instanceof Uint8Array ? data : new Uint8Array(data)
                };
            }
        default:
            await ensureOxipngReady();
            {
                const data = optimiseOxiPngRaw(
                    imageData.data,
                    imageData.width,
                    imageData.height,
                    OXIPNG_DEFAULTS.level,
                    OXIPNG_DEFAULTS.interlace,
                    OXIPNG_DEFAULTS.optimiseAlpha
                );
                return {
                    mimeType: 'image/png',
                    data: data instanceof Uint8Array ? data : new Uint8Array(data)
                };
            }
    }
}

// High-quality resize and encoding with jSquash or Photon
async function processImage(img, options = {}) {
    try {
        const { cancelOnChange = false, snapshot, yieldMs = 0, shouldCancel = null } = options;
        const processingSnapshot = snapshot || createProcessingSnapshot(img);
        if (cancelOnChange) {
            assertNotInterrupted(processingSnapshot, img);
        }
        assertNotCancelled(shouldCancel);
        const { usePhotonResize, jsquashMethod, photonSamplingFilter } = getResizeEngineSettings();
        const resizeEngine = usePhotonResize ? 'Photon' : 'jSquash';
        console.log(`=== Processing Image with ${resizeEngine} ===`);
        console.time('processImage total');
        const resizeMode = getResizeMode();
        const { width: exportWidth, height: exportHeight, shouldResize, crop } = getExportOutputDimensions(img, resizeMode);
        const format = document.getElementById('exportFormat').value;
        console.log(`Target: ${exportWidth}x${exportHeight}, Format: ${format}`);

        const fileToProcess = img.file;
        if (!fileToProcess) {
            throw new Error('No file available to process');
        }

        const cropRect = crop || img.crop;
        console.log(`Source crop: ${cropRect.width}x${cropRect.height} at (${cropRect.x}, ${cropRect.y})`);

        console.time('processImage decode');
        const imageBitmap = await createImageBitmap(fileToProcess);
        console.timeEnd('processImage decode');
        if (yieldMs > 0) {
            await yieldToUI(yieldMs);
        }
        if (cancelOnChange) {
            assertNotInterrupted(processingSnapshot, img);
        }
        assertNotCancelled(shouldCancel);
        console.time('processImage crop');
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = Math.round(cropRect.width);
        cropCanvas.height = Math.round(cropRect.height);
        const cropCtx = cropCanvas.getContext('2d', { alpha: true });
        cropCtx.drawImage(
            imageBitmap,
            cropRect.x, cropRect.y, cropRect.width, cropRect.height,
            0, 0, cropCanvas.width, cropCanvas.height
        );
        imageBitmap.close();

        const croppedImageData = cropCtx.getImageData(0, 0, cropCanvas.width, cropCanvas.height);
        console.timeEnd('processImage crop');
        console.log(`Cropped ImageData: ${croppedImageData.width}x${croppedImageData.height}`);
        if (yieldMs > 0) {
            await yieldToUI(yieldMs);
        }
        if (cancelOnChange) {
            assertNotInterrupted(processingSnapshot, img);
        }
        assertNotCancelled(shouldCancel);

        let resizedImageData = croppedImageData;
        const needsResize = shouldResize
            && exportWidth
            && exportHeight
            && (croppedImageData.width !== exportWidth || croppedImageData.height !== exportHeight);
        if (needsResize) {
            try {
                console.time('processImage resize');
                const forcedFallback = getForcedFallbackMode();
                if (forcedFallback === 'resize' || forcedFallback === 'both') {
                    throw new Error('Forced fallback (resize)');
                }
                if (usePhotonResize) {
                    resizedImageData = await resizeWithPhoton(
                        croppedImageData,
                        exportWidth,
                        exportHeight,
                        photonSamplingFilter
                    );
                    console.log('Photon resize complete');
                } else {
                    resizedImageData = await jsquashResize(croppedImageData, {
                        width: exportWidth,
                        height: exportHeight,
                        method: jsquashMethod || DEFAULT_JSQUASH_METHOD
                    });
                    console.log('jSquash resize complete');
                }
                console.timeEnd('processImage resize');
            } catch (resizeError) {
                console.timeEnd('processImage resize');
                console.warn(`${resizeEngine} resize failed, falling back to canvas resize`, resizeError);
                setFallbackWarning('resize');
                console.time('processImage resizeCanvas');
                resizedImageData = resizeWithCanvas(cropCanvas, exportWidth, exportHeight);
                console.timeEnd('processImage resizeCanvas');
            }
            if (yieldMs > 0) {
                await yieldToUI(yieldMs);
            }
        } else if (!shouldResize) {
            console.log(`Resize skipped (${getResizeMode()})`);
        } else if (shouldResize && exportWidth && exportHeight) {
            console.log('Resize skipped (already at target size)');
        }
        if (yieldMs > 0) {
            await yieldToUI(yieldMs);
        }
        if (cancelOnChange) {
            assertNotInterrupted(processingSnapshot, img);
        }
        assertNotCancelled(shouldCancel);

        try {
            const enableSharpening = Boolean(document.getElementById('enableSharpening')?.checked);
            let finalImageData = resizedImageData;
            if (enableSharpening) {
                console.time('processImage sharpen');
                finalImageData = await applySharpening(resizedImageData);
                console.timeEnd('processImage sharpen');
                if (yieldMs > 0) {
                    await yieldToUI(yieldMs);
                }
                if (cancelOnChange) {
                    assertNotInterrupted(processingSnapshot, img);
                }
                assertNotCancelled(shouldCancel);
            } else {
                console.log('Sharpening skipped (disabled)');
            }
            console.time('processImage encode');
            const forcedFallback = getForcedFallbackMode();
            if (forcedFallback === 'encode' || forcedFallback === 'both') {
                throw new Error('Forced fallback (encode)');
            }
            const { data, mimeType } = await encodeWithJsquash(finalImageData, format);
            console.timeEnd('processImage encode');
            const blob = new Blob([data], { type: mimeType });
            console.log(`${resizeEngine} blob created: ${blob.size} bytes`);
            console.log('=== Processing Complete ===');
            console.timeEnd('processImage total');
            return blob;
        } catch (encodeError) {
            console.warn(`${resizeEngine} encode failed, falling back to canvas encoding`, encodeError);
            setFallbackWarning('encode');
            console.time('processImage encodeFallback');
            const blob = await encodeWithCanvasFallback(resizedImageData, format);
            console.timeEnd('processImage encodeFallback');
            console.log(`Fallback blob created: ${blob.size} bytes`);
            console.log('=== Processing Complete ===');
            console.timeEnd('processImage total');
            return blob;
        }
    } catch (err) {
        console.timeEnd('processImage total');
        console.error('Process image error:', err);
        throw err;
    }
}

async function exportImages() {
    if (exportInProgress || images.length === 0) return;
    exportCancelRequested = false;
    setExportingState(true);

    const isSingleExport = images.length === 1;
    const zip = isSingleExport ? null : new JSZip();
    const usedFilenames = isSingleExport ? null : new Set();
    const format = document.getElementById('exportFormat').value;
    const progress = document.getElementById('exportProgress');
    const progressBar = document.getElementById('exportProgressBar');
    const progressText = document.getElementById('exportProgressText');
    let processedCount = 0;
    let singleExportBlob = null;
    let singleExportFilename = '';
    if (progress) {
        progress.classList.add('active');
    }
    if (progressBar) {
        progressBar.style.width = '0%';
    }
    if (progressText) {
        progressText.textContent = `Processing Image 1/${images.length}`;
    }

    try {
        for (let i = 0; i < images.length; i++) {
            if (exportCancelRequested) {
                assertNotCancelled(() => true);
            }
            const img = images[i];
            const blob = await processImage(img, {
                cancelOnChange: false,
                shouldCancel: () => exportCancelRequested,
                yieldMs: 1
            });

            const nameWithoutExt = img.name.replace(/\.[^/.]+$/, "");
            const baseFilename = `${nameWithoutExt}.${format}`;
            const filename = isSingleExport
                ? baseFilename
                : getUniqueFilename(baseFilename, usedFilenames);

            if (isSingleExport) {
                singleExportBlob = blob;
                singleExportFilename = filename;
            } else {
                zip.file(filename, blob);
            }
            processedCount += 1;
            if (progressBar) {
                progressBar.style.width = `${Math.round(((i + 1) / images.length) * 100)}%`;
            }
            if (progressText) {
                progressText.textContent = `Processing Image ${i + 1}/${images.length}`;
            }
        }

        if (exportCancelRequested) {
            assertNotCancelled(() => true);
        }

        if (progressText) {
            progressText.textContent = 'Preparing download...';
        }

        const content = isSingleExport ? singleExportBlob : await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = isSingleExport ? singleExportFilename : 'processed-images.zip';
        a.click();
        URL.revokeObjectURL(url);
        clearDirtyState();
        if (progressBar) {
            progressBar.style.width = '100%';
        }
    } catch (err) {
        if (err?.code === 'CANCELLED') {
            if (isSingleExport) {
                if (progressText) {
                    progressText.textContent = 'Export cancelled';
                }
                if (progressBar) {
                    progressBar.style.width = '0%';
                }
            } else if (processedCount > 0) {
                const shouldDownload = window.confirm('Download images produced so far?');
                if (shouldDownload) {
                    if (progressText) {
                        progressText.textContent = 'Preparing download...';
                    }
                    const content = await zip.generateAsync({ type: 'blob' });
                    const url = URL.createObjectURL(content);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'processed-images-partial.zip';
                    a.click();
                    URL.revokeObjectURL(url);
                    if (progressBar) {
                        progressBar.style.width = '100%';
                    }
                } else {
                    if (progressText) {
                        progressText.textContent = 'Export cancelled';
                    }
                    if (progressBar) {
                        progressBar.style.width = '0%';
                    }
                }
            } else {
                if (progressText) {
                    progressText.textContent = 'Export cancelled';
                }
                if (progressBar) {
                    progressBar.style.width = '0%';
                }
            }
        } else {
            console.error('Export failed:', err);
            if (progressText) {
                progressText.textContent = 'Export failed';
            }
        }
    } finally {
        exportCancelRequested = false;
        setExportingState(false);
        if (progress) {
            setTimeout(() => {
                progress.classList.remove('active');
                if (progressBar) {
                    progressBar.style.width = '0%';
                }
                if (progressText) {
                    progressText.textContent = '';
                }
            }, 400);
        }
    }
}

function getUniqueFilename(filename, usedFilenames) {
    if (!usedFilenames.has(filename)) {
        usedFilenames.add(filename);
        return filename;
    }
    const lastDotIndex = filename.lastIndexOf('.');
    const hasExt = lastDotIndex > 0 && lastDotIndex < filename.length - 1;
    const base = hasExt ? filename.slice(0, lastDotIndex) : filename;
    const ext = hasExt ? filename.slice(lastDotIndex) : '';
    let counter = 2;
    let candidate = `${base} (${counter})${ext}`;
    while (usedFilenames.has(candidate)) {
        counter += 1;
        candidate = `${base} (${counter})${ext}`;
    }
    usedFilenames.add(candidate);
    return candidate;
}

function adjustZoom(delta) {
    const step = 40;
    const direction = delta >= 0 ? 1 : -1;
    gridItemSize = Math.max(160, Math.min(800, gridItemSize + direction * step));
    updateGridSize();
    persistPreferencesNoDirty({ gridItemSize });
}

function resetZoom() {
    gridItemSize = 240;
    updateGridSize();
    persistPreferencesNoDirty({ gridItemSize });
}

function toggleTheme() {
    const enabled = document.getElementById('themeToggle')?.checked;
    document.documentElement.classList.toggle('light', Boolean(enabled));
    document.body.classList.toggle('light', Boolean(enabled));
    persistPreferencesNoDirty();
}

function updateGridSize() {
    const grid = document.getElementById('imagesGrid');
    if (!grid) return;
    grid.style.setProperty('--grid-image-size', `${gridItemSize}px`);
    grid.style.gridTemplateColumns = `repeat(auto-fill, minmax(${gridItemSize}px, 1fr))`;
    if (showFilenameTip) {
        positionFilenameTip();
    }
}

function alignAllCrops(alignment) {
    images.forEach(img => {
        switch(alignment) {
            case 'top':
                img.crop.y = 0;
                break;
            case 'middle':
                img.crop.y = (img.height - img.crop.height) / 2;
                break;
            case 'bottom':
                img.crop.y = img.height - img.crop.height;
                break;
        }
    });
    updateAllCropBoxes();
}

function setAspectRatio(width, height, buttonEl) {
    const mode = getResizeMode();
    if (mode !== RESIZE_MODES.FIXED_ASPECT && mode !== RESIZE_MODES.FIXED_ASPECT_CROP) return;
    currentAspectRatio = width / height;

    // Update all crops to new aspect ratio
    images.forEach(img => {
        img.crop = calculateInitialCrop(img.width, img.height);
    });

    if (mode === RESIZE_MODES.FIXED_ASPECT || mode === RESIZE_MODES.FIXED_ASPECT_CROP) {
        // Update export dimensions
        const exportWidth = parseInt(document.getElementById('exportWidth').value, 10);
        if (Number.isFinite(exportWidth) && exportWidth > 0) {
            const exportHeight = Math.round(exportWidth / currentAspectRatio);
            document.getElementById('exportHeight').value = exportHeight;
        }
    }

    // Update active button
    updateActiveAspectButton(buttonEl, width, height);
    const aspectW = document.getElementById('aspectW');
    const aspectH = document.getElementById('aspectH');
    if (aspectW && aspectH) {
        aspectW.value = width;
        aspectH.value = height;
    }
    updateAspectMismatchUI();
    persistPreferences({ aspectRatio: { width, height } });
    bumpExportSettingsChange();
    markProfileModified();

    updateAllCropBoxes();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

function handleResizeModeChange() {
    updateResizeModeUI();
    updateResizeModeInfoText();
    updatePreviewNudgeControls();
    persistPreferences();
    markProfileModified();
    bumpExportSettingsChange();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

function updateFormatSettings(shouldPersist = true) {
    const format = document.getElementById('exportFormat').value;

    document.getElementById('jpegSettings').style.display = format === 'jpeg' ? 'block' : 'none';
    document.getElementById('pngSettings').style.display = format === 'png' ? 'block' : 'none';
    document.getElementById('webpSettings').style.display = format === 'webp' ? 'block' : 'none';
    document.getElementById('avifSettings').style.display = format === 'avif' ? 'block' : 'none';
    document.getElementById('gifSettings').style.display = format === 'gif' ? 'block' : 'none';
    if (shouldPersist) {
        persistPreferences();
        markProfileModified();
    }
    bumpExportSettingsChange();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

function toggleDrawer() {
    const drawer = document.getElementById('drawer');
    const icon = document.getElementById('drawerToggleIcon');
    drawer.classList.toggle('collapsed');
    icon.textContent = drawer.classList.contains('collapsed') ? '▶' : '◀';
}

function updateExportButton() {
    document.getElementById('exportBtn').disabled = images.length === 0 || exportInProgress;
}

function updateExportCancelButton() {
    const cancelBtn = document.getElementById('exportCancelBtn');
    if (!cancelBtn) return;
    cancelBtn.disabled = !exportInProgress || exportCancelRequested;
    cancelBtn.textContent = exportCancelRequested ? 'Cancelling...' : 'Cancel';
}

function setExportingState(isExporting) {
    exportInProgress = isExporting;
    document.body.classList.toggle('exporting', isExporting);
    updateExportButton();
    updateExportCancelButton();
}

function cancelExport() {
    if (!exportInProgress) return;
    exportCancelRequested = true;
    updateExportCancelButton();
    const progressText = document.getElementById('exportProgressText');
    if (progressText) {
        progressText.textContent = 'Cancelling...';
    }
}

function removeImage(id) {
    images = images.filter(img => img.id !== id);
    const grid = document.getElementById('imagesGrid');
    if (grid) {
        const card = grid.querySelector(`[data-image-id="${id}"]`);
        if (card) {
            card.remove();
        }
    }
    if (selectedImageId === id) {
        selectedImageId = null;
        closeExportPreview();
        closePreview();
        if (isSplitPreview) {
            setSplitPreview(false);
        }
    }
    updateExportButton();
    if (images.length === 0) {
        if (dropZone) {
            dropZone.classList.remove('hidden');
        }
        syncImagesVisibility();
        forceResetPreviewState();
    }
    setDirtyState(true);
}

function clearAllImages() {
    images = [];
    selectedImageId = null;
    closeExportPreview();
    closePreview();
    renderImages();
    updateExportButton();
    if (dropZone) {
        dropZone.classList.remove('hidden');
    }
    syncImagesVisibility();
    forceResetPreviewState();
    setDirtyState(true);
}

function handleResizeSettingsChange() {
    syncResizeEngineControls();
    persistPreferences();
    bumpExportSettingsChange();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
}

// Update dimensions when typing
document.getElementById('exportWidth').addEventListener('input', () => {
    const mode = getResizeMode();
    if (mode === RESIZE_MODES.FIXED_ASPECT) {
        const width = parseInt(document.getElementById('exportWidth').value);
        const height = Math.round(width / currentAspectRatio);
        document.getElementById('exportHeight').value = height;
    } else if (mode === RESIZE_MODES.STATIC_SIZE) {
        updateAspectInputsFromCurrent();
        applyStaticSizeAspect();
    }
    updateAspectMismatchUI();
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});

document.getElementById('exportHeight').addEventListener('input', () => {
    const mode = getResizeMode();
    if (mode === RESIZE_MODES.FIXED_ASPECT) {
        const height = parseInt(document.getElementById('exportHeight').value);
        const width = Math.round(height * currentAspectRatio);
        document.getElementById('exportWidth').value = width;
    } else if (mode === RESIZE_MODES.STATIC_SIZE) {
        updateAspectInputsFromCurrent();
        applyStaticSizeAspect();
    }
    updateAspectMismatchUI();
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});

document.getElementById('jpegQuality').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('jpegQualityNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('webpQuality').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('webpQualityNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('avifQuality').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('avifQualityNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('pngTransparency').addEventListener('change', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('previewDelay').addEventListener('change', () => {
    persistPreferencesNoDirty();
});
document.getElementById('resizeMode').addEventListener('change', handleResizeModeChange);
document.getElementById('resizeEngine').addEventListener('change', handleResizeSettingsChange);
document.getElementById('jsquashResizeMethod').addEventListener('change', handleResizeSettingsChange);
document.getElementById('photonResizeFilter').addEventListener('change', handleResizeSettingsChange);
document.getElementById('scalePercent').addEventListener('input', () => {
    handleScalePercentInput(true);
});
document.getElementById('aspectW').addEventListener('input', applyAspectRatioFromInputs);
document.getElementById('aspectH').addEventListener('input', applyAspectRatioFromInputs);
document.getElementById('aboutAutoShow').addEventListener('change', () => {
    persistPreferencesNoDirty();
});
document.getElementById('enableSharpening').addEventListener('change', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('adaptiveSharpening').addEventListener('change', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('sharpenAmount').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('sharpenRadius').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('sharpenThreshold').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('sharpenAmountNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('previewFooter').addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-crop-action]');
    if (!btn) return;
    e.preventDefault();
    const step = (e.shiftKey || nudgeBoostEnabled) ? 10 : 1;
    nudgeCrop(btn.dataset.cropAction, step);
});
document.getElementById('nudgeBoostToggle')?.addEventListener('click', (e) => {
    e.preventDefault();
    nudgeBoostEnabled = !nudgeBoostEnabled;
    const btn = e.currentTarget;
    btn.classList.toggle('active', nudgeBoostEnabled);
    btn.setAttribute('aria-pressed', String(nudgeBoostEnabled));
});
document.getElementById('filenameTipDismiss').addEventListener('click', (e) => {
    e.preventDefault();
    dismissFilenameTip();
});
document.getElementById('gridContainer').addEventListener('scroll', () => {
    if (showFilenameTip) {
        positionFilenameTip();
    }
});
document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
        scheduleGridRefreshAfterResume();
    }
});
window.addEventListener('pageshow', () => {
    scheduleGridRefreshAfterResume();
});
window.addEventListener('resize', () => {
    if (showFilenameTip) {
        positionFilenameTip();
    }
});
document.getElementById('profileImportInput').addEventListener('change', async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;
    try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) {
            window.alert('Invalid profiles JSON. Expected an array of profiles.');
            return;
        }
        const importedProfiles = parsed.filter(profile => profile && typeof profile === 'object');
        if (!importedProfiles.length) {
            window.alert('No valid profiles found in the JSON.');
            return;
        }
        const existingProfiles = getProfiles();
        const duplicates = collectDuplicateNames(importedProfiles, existingProfiles);
        if (duplicates.length) {
            const message = `Duplicate profile names detected:\n${duplicates.join(', ')}\n\nIf you continue, the current profiles will be overridden.`;
            const shouldContinue = window.confirm(message);
            if (!shouldContinue) {
                return;
            }
        }
        const mergedProfiles = mergeImportedProfiles(importedProfiles, existingProfiles);
        setProfiles(mergedProfiles);
        if (profilesVisible) {
            renderProfiles();
        }
    } catch (err) {
        console.error('Profile import failed', err);
        window.alert('Failed to import profiles. Please check the file and try again.');
    } finally {
        input.value = '';
    }
});
document.getElementById('sharpenRadiusNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
document.getElementById('sharpenThresholdNumber').addEventListener('input', () => {
    persistPreferences();
    bumpExportSettingsChange();
    markProfileModified();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
});
window.addEventListener('resize', updatePreviewZoomLabels);
bindRangeAndNumber('jpegQuality', 'jpegQualityNumber', (v) => parseInt(v, 10));
bindRangeAndNumber('webpQuality', 'webpQualityNumber', (v) => parseInt(v, 10));
bindRangeAndNumber('avifQuality', 'avifQualityNumber', (v) => parseInt(v, 10));
bindRangeAndNumber('sharpenAmount', 'sharpenAmountNumber', (v) => parseInt(v, 10));
bindRangeAndNumber('sharpenRadius', 'sharpenRadiusNumber', (v) => parseFloat(v));
bindRangeAndNumber('sharpenThreshold', 'sharpenThresholdNumber', (v) => parseInt(v, 10));

function getPreferences() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (err) {
        return {};
    }
}

function getProfiles() {
    const prefs = getPreferences();
    if (!Array.isArray(prefs.profiles)) {
        return [];
    }
    return prefs.profiles;
}

function setProfiles(profiles) {
    persistPreferences({ profiles });
    updateProfileImportExportButtons();
}

function mergeProfileDefaults(profile) {
    const merged = { ...profileDefault, ...profile };
    if (profileDefault.aspectRatio || profile.aspectRatio) {
        merged.aspectRatio = {
            ...(profileDefault.aspectRatio || {}),
            ...(profile.aspectRatio || {})
        };
    }
    return merged;
}

function buildDefaultProfileName() {
    const width = document.getElementById('exportWidth').value;
    const height = document.getElementById('exportHeight').value;
    const scalePercent = getScalePercent();
    const format = document.getElementById('exportFormat').value.toUpperCase();
    const mode = getResizeMode();
    const aspectW = parseFloat(document.getElementById('aspectW').value);
    const aspectH = parseFloat(document.getElementById('aspectH').value);
    const hasAspectInputs = Number.isFinite(aspectW) && Number.isFinite(aspectH) && aspectH > 0;
    let aspect = '';
    if ((mode === RESIZE_MODES.FIXED_ASPECT || mode === RESIZE_MODES.STATIC_SIZE) && width && height) {
        aspect = `${width}:${height}`;
    } else if (mode === RESIZE_MODES.FIXED_ASPECT_CROP && hasAspectInputs) {
        aspect = `${aspectW}:${aspectH}`;
    }
    const parts = [];
    if (mode === RESIZE_MODES.SCALED) {
        parts.push(`${scalePercent}%`);
    } else if (width && height && mode !== RESIZE_MODES.FIXED_ASPECT_CROP) {
        parts.push(`${width}x${height}`);
    }
    if (aspect) {
        parts.push(`${aspect}`);
    }
    parts.push(format);
    return parts.join(' ');
}

function formatAspectRatio(profile) {
    if (profile.aspectRatio && profile.aspectRatio.width && profile.aspectRatio.height) {
        const w = profile.aspectRatio.width;
        const h = profile.aspectRatio.height;
        const { arW, arH } = aspectRatioExact(w, h, { includePortrait: false });
        for (const preset of ASPECT_PRESETS) {
            if (preset.w === arW && preset.h === arH) {
                return preset.label || `${preset.w}:${preset.h}`;
            }
        }
        return simplifyRatioString(w, h);
    }
    if (profile.exportWidth && profile.exportHeight) {
        return simplifyRatioString(profile.exportWidth, profile.exportHeight);
    }
    return '';
}

function renderAspectPresetButtons() {
    const select = document.getElementById('aspectPresetSelect');
    if (!select) return;
    select.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select preset';
    select.appendChild(placeholder);
    ASPECT_PRESETS.slice(0, 30).forEach(preset => {
        const option = document.createElement('option');
        option.value = `${preset.w}:${preset.h}`;
        option.dataset.aspectW = preset.w;
        option.dataset.aspectH = preset.h;
        option.textContent = preset.label || `${preset.w}:${preset.h}`;
        select.appendChild(option);
    });
    select.addEventListener('change', () => {
        const selected = select.selectedOptions[0];
        if (!selected || !selected.dataset.aspectW || !selected.dataset.aspectH) return;
        setAspectRatio(parseInt(selected.dataset.aspectW), parseInt(selected.dataset.aspectH));
    });
}

function updateProfileNameDisplay() {
    const input = document.getElementById('profileNameDisplay');
    if (!input) return;
    if (!currentProfileName) {
        input.textContent = '(no profile selected)';
        input.classList.add('muted');
    } else {
        input.textContent = profileModified ? `${currentProfileName} (modified)` : currentProfileName;
        input.classList.remove('muted');
    }
    const clearAction = document.getElementById('profileClearAction');
    const revertAction = document.getElementById('profileRevertAction');
    if (clearAction) {
        clearAction.style.display = currentProfileName ? 'inline' : 'none';
    }
    if (revertAction) {
        revertAction.style.display = profileModified && lastLoadedProfile ? 'inline' : 'none';
    }
}

function markProfileModified() {
    if (suppressProfileModified) return;
    profileModified = true;
    updateProfileNameDisplay();
}

function captureCurrentProfileSettings() {
    return {
        exportWidth: parseInt(document.getElementById('exportWidth').value),
        exportHeight: parseInt(document.getElementById('exportHeight').value),
        scalePercent: getScalePercent(),
        resizeMode: getResizeMode(),
        aspectRatio: { width: currentAspectRatio * 1000, height: 1000 },
        exportFormat: document.getElementById('exportFormat').value,
        jpegQuality: parseInt(document.getElementById('jpegQuality').value),
        webpQuality: parseInt(document.getElementById('webpQuality').value),
        avifQuality: parseInt(document.getElementById('avifQuality').value),
        pngTransparency: document.getElementById('pngTransparency').checked,
        enableSharpening: document.getElementById('enableSharpening').checked,
        adaptiveSharpening: document.getElementById('adaptiveSharpening').checked,
        sharpenAmount: parseInt(document.getElementById('sharpenAmount').value),
        sharpenRadius: parseFloat(document.getElementById('sharpenRadius').value),
        sharpenThreshold: parseInt(document.getElementById('sharpenThreshold').value)
    };
}

function applyProfileSettings(profile, options = {}) {
    if (!profile) return;
    const { applyDefaults = false, setProfileName = true, setLastLoaded = true } = options;
    const resolvedProfile = applyDefaults ? mergeProfileDefaults(profile) : profile;
    let resizeMode = resolvedProfile.resizeMode;
    if (!resizeMode && typeof resolvedProfile.lockAspect === 'boolean') {
        resizeMode = resolvedProfile.lockAspect ? RESIZE_MODES.FIXED_ASPECT : RESIZE_MODES.STATIC_SIZE;
    }
    resizeMode = resizeMode || DEFAULT_RESIZE_MODE;
    suppressProfileModified = true;
    if (Number.isFinite(resolvedProfile.exportWidth)) {
        document.getElementById('exportWidth').value = resolvedProfile.exportWidth;
    }
    if (Number.isFinite(resolvedProfile.exportHeight)) {
        document.getElementById('exportHeight').value = resolvedProfile.exportHeight;
    }
    if (Number.isFinite(resolvedProfile.scalePercent)) {
        const scaleInput = document.getElementById('scalePercent');
        if (scaleInput) {
            scaleInput.value = clampScalePercent(resolvedProfile.scalePercent);
        }
    }
    const resizeModeSelect = document.getElementById('resizeMode');
    if (resizeModeSelect) {
        resizeModeSelect.value = resizeMode;
        if (!resizeModeSelect.value) {
            resizeModeSelect.value = DEFAULT_RESIZE_MODE;
            resizeMode = DEFAULT_RESIZE_MODE;
        }
    }
    if (resolvedProfile.exportFormat) {
        document.getElementById('exportFormat').value = resolvedProfile.exportFormat;
    }
    if (Number.isFinite(resolvedProfile.jpegQuality)) {
        document.getElementById('jpegQuality').value = resolvedProfile.jpegQuality;
        document.getElementById('jpegQualityNumber').value = resolvedProfile.jpegQuality;
    }
    if (Number.isFinite(resolvedProfile.webpQuality)) {
        document.getElementById('webpQuality').value = resolvedProfile.webpQuality;
        document.getElementById('webpQualityNumber').value = resolvedProfile.webpQuality;
    }
    if (Number.isFinite(resolvedProfile.avifQuality)) {
        document.getElementById('avifQuality').value = resolvedProfile.avifQuality;
        document.getElementById('avifQualityNumber').value = resolvedProfile.avifQuality;
    }
    if (typeof resolvedProfile.pngTransparency === 'boolean') {
        document.getElementById('pngTransparency').checked = resolvedProfile.pngTransparency;
    }
    if (typeof resolvedProfile.enableSharpening === 'boolean') {
        document.getElementById('enableSharpening').checked = resolvedProfile.enableSharpening;
    }
    if (typeof resolvedProfile.adaptiveSharpening === 'boolean') {
        document.getElementById('adaptiveSharpening').checked = resolvedProfile.adaptiveSharpening;
    }
    if (Number.isFinite(resolvedProfile.sharpenAmount)) {
        document.getElementById('sharpenAmount').value = resolvedProfile.sharpenAmount;
        document.getElementById('sharpenAmountNumber').value = resolvedProfile.sharpenAmount;
    }
    if (Number.isFinite(resolvedProfile.sharpenRadius)) {
        document.getElementById('sharpenRadius').value = resolvedProfile.sharpenRadius;
        document.getElementById('sharpenRadiusNumber').value = resolvedProfile.sharpenRadius;
    }
    if (Number.isFinite(resolvedProfile.sharpenThreshold)) {
        document.getElementById('sharpenThreshold').value = resolvedProfile.sharpenThreshold;
        document.getElementById('sharpenThresholdNumber').value = resolvedProfile.sharpenThreshold;
    }

    if (resolvedProfile.aspectRatio && Number.isFinite(resolvedProfile.aspectRatio.width) && Number.isFinite(resolvedProfile.aspectRatio.height)) {
        currentAspectRatio = resolvedProfile.aspectRatio.width / resolvedProfile.aspectRatio.height;
        updateActiveAspectButton(null, resolvedProfile.aspectRatio.width, resolvedProfile.aspectRatio.height);
        const aspectW = document.getElementById('aspectW');
        const aspectH = document.getElementById('aspectH');
        if (aspectW && aspectH) {
            aspectW.value = resolvedProfile.aspectRatio.width;
            aspectH.value = resolvedProfile.aspectRatio.height;
        }
    } else {
        const width = parseInt(document.getElementById('exportWidth').value);
        const height = parseInt(document.getElementById('exportHeight').value);
        if (width && height) {
            currentAspectRatio = width / height;
            updateAspectInputsFromCurrent();
        }
    }

    if (isAspectLockedMode(resizeMode)) {
        images.forEach(img => {
            img.crop = calculateInitialCrop(img.width, img.height);
        });
    }

    updateFormatSettings(false);
    handleScalePercentInput(false);
    lastResizeMode = resizeMode;
    updateResizeModeUI();
    persistPreferences();
    if (shouldShowCropOverlay(resizeMode)) {
        updateAllCropBoxes();
    }
    bumpExportSettingsChange();
    scheduleExportPreviewRefresh({ preserveImage: true }, getPreviewDelayMs(true));
    currentProfileName = setProfileName ? (resolvedProfile.name || '') : '';
    profileModified = false;
    lastLoadedProfile = setLastLoaded ? { ...resolvedProfile } : null;
    updateProfileNameDisplay();
    suppressProfileModified = false;
}

function clearProfileName() {
    currentProfileName = '';
    profileModified = false;
    updateProfileNameDisplay();
}

function revertProfileSettings() {
    if (!lastLoadedProfile) return;
    applyProfileSettings(lastLoadedProfile);
}

function resetProfileToDefault() {
    applyProfileSettings(profileDefault, {
        applyDefaults: true,
        setProfileName: false,
        setLastLoaded: false
    });
}

function renderProfiles() {
    const list = document.getElementById('profilesList');
    if (!list) return;
    const profiles = getProfiles();
    list.innerHTML = '';
    updateProfileImportExportButtons();
    const createFoldableSection = (titleText, isOpen) => {
        const section = document.createElement('section');
        section.className = `profiles-section ${isOpen ? 'expanded' : 'collapsed'}`;

        const title = document.createElement('button');
        title.type = 'button';
        title.className = 'profiles-section-title';
        title.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        const label = document.createElement('span');
        label.className = 'profiles-section-label';
        label.textContent = titleText;
        title.appendChild(label);

        const chevron = document.createElement('span');
        chevron.className = 'profiles-section-chevron';
        chevron.textContent = '▾';
        title.appendChild(chevron);

        const body = document.createElement('div');
        body.className = 'profiles-section-body';

        title.onclick = () => {
            const expanded = section.classList.toggle('expanded');
            section.classList.toggle('collapsed', !expanded);
            title.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        section.appendChild(title);
        section.appendChild(body);
        return { section, body };
    };

    const addProfileCard = (profile, idx, allowDelete) => {
        const card = document.createElement('div');
        card.className = 'profile-card';
        const info = document.createElement('div');
        info.className = 'profile-info';
        const name = document.createElement('div');
        name.className = 'profile-name';
        name.textContent = profile.name || `Profile ${idx + 1}`;
        const meta = document.createElement('div');
        meta.className = 'profile-meta';
        const aspect = formatAspectRatio(profile);
        const parts = [
            `${profile.exportWidth || ''}x${profile.exportHeight || ''}`.trim(),
            String(profile.exportFormat || '').toUpperCase(),
            aspect
        ].filter(Boolean);
        if (profile.enableSharpening) {
            parts.push('Sharpening');
        }
        if (profile.resizeMode === RESIZE_MODES.SCALED && Number.isFinite(profile.scalePercent)) {
            parts.push(`${clampScalePercent(profile.scalePercent)}%`);
        }
        meta.textContent = parts.join(' · ');
        info.appendChild(name);
        info.appendChild(meta);
        const actions = document.createElement('div');
        actions.className = 'profile-actions';
        const loadBtn = document.createElement('button');
        loadBtn.className = 'btn';
        loadBtn.textContent = 'Load';
        loadBtn.onclick = () => {
            applyProfileSettings(profile);
            if (profilesVisible) {
                toggleProfiles();
            }
        };
        actions.appendChild(loadBtn);
        if (allowDelete) {
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'btn';
            deleteBtn.textContent = 'Delete';
            deleteBtn.onclick = () => {
                const profileName = profile.name || `Profile ${idx + 1}`;
                const confirmed = window.confirm(`Delete profile "${profileName}"?`);
                if (!confirmed) return;
                const next = getProfiles().filter((_, pIdx) => pIdx !== idx);
                setProfiles(next);
                renderProfiles();
            };
            actions.appendChild(deleteBtn);
        }
        card.appendChild(info);
        card.appendChild(actions);
        return card;
    };

    const userList = document.createElement('div');
    userList.className = 'profiles-list';
    if (!profiles.length) {
        const empty = document.createElement('div');
        empty.style.color = 'var(--text-secondary)';
        empty.style.fontSize = '12px';
        empty.textContent = 'No profiles saved yet.';
        userList.appendChild(empty);
    } else {
        profiles.forEach((profile, idx) => userList.appendChild(addProfileCard(profile, idx, true)));
    }
    const userSection = createFoldableSection('User Profiles', true);
    userSection.body.appendChild(userList);
    list.appendChild(userSection.section);

    const groupedDefaults = Array.isArray(DEFAULT_PROFILES)
        ? [{ title: 'Default Profiles', profiles: DEFAULT_PROFILES }]
        : Object.entries(DEFAULT_PROFILES || {})
            .map(([title, groupedProfiles]) => ({
                title,
                profiles: Array.isArray(groupedProfiles) ? groupedProfiles : []
            }))
            .filter(group => group.profiles.length > 0);

    groupedDefaults.forEach((group, groupIdx) => {
        const groupList = document.createElement('div');
        groupList.className = 'profiles-list';
        group.profiles.forEach((profile, idx) => {
            const card = addProfileCard(profile, idx, false);
            const loadBtn = card.querySelector('button.btn');
            if (loadBtn) {
                loadBtn.onclick = () => {
                    applyProfileSettings(profile, { applyDefaults: true });
                    if (profilesVisible) {
                        toggleProfiles();
                    }
                };
            }
            groupList.appendChild(card);
        });
        const section = createFoldableSection(group.title || `Profiles ${groupIdx + 1}`, false);
        section.body.appendChild(groupList);
        list.appendChild(section.section);
    });
}

function updateProfileImportExportButtons() {
    const exportBtn = document.getElementById('exportProfilesBtn');
    if (!exportBtn) return;
    const profiles = getProfiles();
    exportBtn.disabled = !profiles.length;
}

function exportUserProfiles() {
    const profiles = getProfiles();
    if (!profiles.length) return;
    const payload = JSON.stringify(profiles, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'batch-image-profiles.json';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function importUserProfiles() {
    const input = document.getElementById('profileImportInput');
    if (!input) return;
    input.value = '';
    input.click();
}

function normalizeProfileName(profile) {
    if (!profile || typeof profile !== 'object') return '';
    if (typeof profile.name !== 'string') return '';
    return profile.name.trim();
}

function collectDuplicateNames(importedProfiles, existingProfiles) {
    const duplicates = new Set();
    const seenImported = new Set();
    const existingNames = new Set(existingProfiles.map(normalizeProfileName).filter(Boolean));

    importedProfiles.forEach((profile) => {
        const name = normalizeProfileName(profile);
        if (!name) return;
        if (seenImported.has(name) || existingNames.has(name)) {
            duplicates.add(name);
        }
        seenImported.add(name);
    });

    return Array.from(duplicates);
}

function mergeImportedProfiles(importedProfiles, existingProfiles) {
    const merged = [...existingProfiles];
    const nameIndex = new Map();
    merged.forEach((profile, idx) => {
        const name = normalizeProfileName(profile);
        if (name) {
            nameIndex.set(name, idx);
        }
    });

    importedProfiles.forEach((profile) => {
        if (!profile || typeof profile !== 'object') return;
        const name = normalizeProfileName(profile);
        if (name && nameIndex.has(name)) {
            merged[nameIndex.get(name)] = profile;
        } else {
            merged.push(profile);
            if (name) {
                nameIndex.set(name, merged.length - 1);
            }
        }
    });

    return merged;
}

function toggleProfiles() {
    profilesVisible = !profilesVisible;
    const panel = document.getElementById('profilesPanel');
    const btn = document.getElementById('openProfilesBtn');
    const loadBtn = document.getElementById('loadProfileBtn');
    if (panel) {
        panel.classList.toggle('active', profilesVisible);
    }
    syncImagesVisibility();
    if (btn) {
        btn.textContent = profilesVisible ? 'Back to Images' : 'Open Profiles';
        btn.classList.toggle('active', profilesVisible);
    }
    if (loadBtn) {
        loadBtn.textContent = profilesVisible ? 'Back to images' : 'Load Profile';
        loadBtn.classList.toggle('active', profilesVisible);
    }
    if (profilesVisible) {
        renderProfiles();
    }
    updateFilenameTipVisibility();
}

function saveProfile() {
    const defaultName = buildDefaultProfileName();
    const promptName = currentProfileName || defaultName;
    const name = window.prompt('Profile name', promptName);
    if (name === null) return;
    const trimmed = name.trim() || currentProfileName || defaultName;
    const profile = {
        name: trimmed,
        ...captureCurrentProfileSettings()
    };
    const profiles = getProfiles();
    const existingIndex = profiles.findIndex((p) => p.name === trimmed);
    if (existingIndex !== -1) {
        const overwrite = window.confirm(`Profile "${trimmed}" exists. Overwrite it?`);
        if (!overwrite) return;
        profiles.splice(existingIndex, 1, profile);
    } else {
        profiles.push(profile);
    }
    setProfiles(profiles);
    currentProfileName = trimmed;
    profileModified = false;
    lastLoadedProfile = { ...profile };
    updateProfileNameDisplay();
    if (profilesVisible) {
        renderProfiles();
    }
}
function bindRangeAndNumber(rangeId, numberId, parseFn = (v) => Number(v)) {
    const rangeEl = document.getElementById(rangeId);
    const numberEl = document.getElementById(numberId);
    if (!rangeEl || !numberEl) return;

    const min = Number(rangeEl.min);
    const max = Number(rangeEl.max);
    const clamp = (value) => Math.min(max, Math.max(min, value));

    const syncFromRange = () => {
        numberEl.value = rangeEl.value;
    };
    const syncFromNumber = () => {
        const parsed = parseFn(numberEl.value);
        if (!Number.isFinite(parsed)) return;
        const clamped = clamp(parsed);
        rangeEl.value = clamped;
        numberEl.value = clamped;
    };

    rangeEl.addEventListener('input', () => {
        syncFromRange();
    });
    numberEl.addEventListener('input', () => {
        syncFromNumber();
    });

    syncFromRange();
}

function setDirtyState(nextDirty) {
    isDirty = nextDirty;
}

function clearDirtyState() {
    setDirtyState(false);
}

function persistPreferencesNoDirty(patch = {}) {
    const prevSuppress = suppressDirty;
    suppressDirty = true;
    persistPreferences(patch);
    suppressDirty = prevSuppress;
}

function persistPreferences(patch = {}) {
    const prefs = getPreferences();
    const resizeSettings = getResizeEngineSettings();
    const exportWidthInput = document.getElementById('exportWidth');
    const exportHeightInput = document.getElementById('exportHeight');
    const exportWidthValue = parseInt(exportWidthInput?.value);
    const exportHeightValue = parseInt(exportHeightInput?.value);
    const stashedWidth = parseInt(exportWidthInput?.dataset.stashedValue);
    const stashedHeight = parseInt(exportHeightInput?.dataset.stashedValue);
    const resolvedWidth = Number.isFinite(exportWidthValue)
        ? exportWidthValue
        : (Number.isFinite(stashedWidth) ? stashedWidth : null);
    const resolvedHeight = Number.isFinite(exportHeightValue)
        ? exportHeightValue
        : (Number.isFinite(stashedHeight) ? stashedHeight : null);
    const next = {
        ...prefs,
        exportWidth: resolvedWidth,
        exportHeight: resolvedHeight,
        scalePercent: getScalePercent(),
        resizeMode: getResizeMode(),
        exportFormat: document.getElementById('exportFormat').value,
        jpegQuality: parseInt(document.getElementById('jpegQuality').value),
        webpQuality: parseInt(document.getElementById('webpQuality').value),
        avifQuality: parseInt(document.getElementById('avifQuality').value),
        pngTransparency: document.getElementById('pngTransparency').checked,
        previewDelay: document.getElementById('previewDelay').value,
        lightMode: document.body.classList.contains('light'),
        lastExportPreviewMode,
        showAboutOnLoad: document.getElementById('aboutAutoShow').checked,
        enableSharpening: document.getElementById('enableSharpening').checked,
        adaptiveSharpening: document.getElementById('adaptiveSharpening').checked,
        sharpenAmount: parseInt(document.getElementById('sharpenAmount').value),
        sharpenRadius: parseFloat(document.getElementById('sharpenRadius').value),
        sharpenThreshold: parseInt(document.getElementById('sharpenThreshold').value),
        resizeEngine: resizeSettings.engine,
        usePhotonResize: resizeSettings.usePhotonResize,
        jsquashResizeMethod: resizeSettings.jsquashMethod,
        photonSamplingFilter: resizeSettings.photonSamplingFilter,
        showGridSizeInfo,
        showFilenameTip,
        gridItemSize,
        ...patch
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (!suppressDirty) {
        setDirtyState(true);
    }
}

function applyPreferences() {
    const prefs = getPreferences();
    const mobileWarning = document.getElementById('mobileWarning');
    if (mobileWarning) {
        if (prefs.mobileWarningDismissed) {
            mobileWarning.remove();
        } else {
            mobileWarning.classList.remove('hidden');
        }
    }
    document.documentElement.classList.add('no-theme-transition');
    suppressProfileModified = true;
    if (Number.isFinite(prefs.exportWidth)) {
        document.getElementById('exportWidth').value = prefs.exportWidth;
    }
    if (Number.isFinite(prefs.exportHeight)) {
        document.getElementById('exportHeight').value = prefs.exportHeight;
    }
    if (Number.isFinite(prefs.scalePercent)) {
        const scaleInput = document.getElementById('scalePercent');
        if (scaleInput) {
            scaleInput.value = clampScalePercent(prefs.scalePercent);
        }
    }
    let resizeMode = prefs.resizeMode;
    if (!resizeMode && typeof prefs.lockAspect === 'boolean') {
        resizeMode = prefs.lockAspect ? RESIZE_MODES.FIXED_ASPECT : RESIZE_MODES.STATIC_SIZE;
    }
    resizeMode = resizeMode || DEFAULT_RESIZE_MODE;
    const resizeModeSelect = document.getElementById('resizeMode');
    if (resizeModeSelect) {
        resizeModeSelect.value = resizeMode;
        if (!resizeModeSelect.value) {
            resizeModeSelect.value = DEFAULT_RESIZE_MODE;
        }
    }
    if (prefs.exportFormat) {
        document.getElementById('exportFormat').value = prefs.exportFormat;
    }
    if (Number.isFinite(prefs.jpegQuality)) {
        document.getElementById('jpegQuality').value = prefs.jpegQuality;
        document.getElementById('jpegQualityNumber').value = prefs.jpegQuality;
    }
    if (Number.isFinite(prefs.webpQuality)) {
        document.getElementById('webpQuality').value = prefs.webpQuality;
        document.getElementById('webpQualityNumber').value = prefs.webpQuality;
    }
    if (Number.isFinite(prefs.avifQuality)) {
        document.getElementById('avifQuality').value = prefs.avifQuality;
        document.getElementById('avifQualityNumber').value = prefs.avifQuality;
    }
    if (typeof prefs.pngTransparency === 'boolean') {
        document.getElementById('pngTransparency').checked = prefs.pngTransparency;
    }
    if (prefs.previewDelay) {
        document.getElementById('previewDelay').value = prefs.previewDelay;
    } else {
        document.getElementById('previewDelay').value = '0.5';
    }
    showGridSizeInfo = Boolean(prefs.showGridSizeInfo);
    showFilenameTip = prefs.showFilenameTip !== false;
    if (Number.isFinite(prefs.gridItemSize)) {
        gridItemSize = Math.max(160, Math.min(800, Math.round(prefs.gridItemSize)));
    }
    if (prefs.lastExportPreviewMode) {
        lastExportPreviewMode = prefs.lastExportPreviewMode;
        if (lastExportPreviewMode === 'split') {
            lastExportPreviewMode = 'split-horizontal';
        }
    }
    if (typeof prefs.enableSharpening === 'boolean') {
        document.getElementById('enableSharpening').checked = prefs.enableSharpening;
    }
    if (typeof prefs.adaptiveSharpening === 'boolean') {
        document.getElementById('adaptiveSharpening').checked = prefs.adaptiveSharpening;
    }
    if (Number.isFinite(prefs.sharpenAmount)) {
        document.getElementById('sharpenAmount').value = prefs.sharpenAmount;
        document.getElementById('sharpenAmountNumber').value = prefs.sharpenAmount;
    }
    if (Number.isFinite(prefs.sharpenRadius)) {
        document.getElementById('sharpenRadius').value = prefs.sharpenRadius;
        document.getElementById('sharpenRadiusNumber').value = prefs.sharpenRadius;
    }
    if (Number.isFinite(prefs.sharpenThreshold)) {
        document.getElementById('sharpenThreshold').value = prefs.sharpenThreshold;
        document.getElementById('sharpenThresholdNumber').value = prefs.sharpenThreshold;
    }
    let resizeEngine = prefs.resizeEngine;
    if (!resizeEngine) {
        resizeEngine = typeof prefs.usePhotonResize === 'boolean' && prefs.usePhotonResize ? 'photon' : 'jsquash';
    }
    const resizeEngineSelect = document.getElementById('resizeEngine');
    if (resizeEngineSelect) {
        resizeEngineSelect.value = resizeEngine;
        if (!resizeEngineSelect.value) {
            resizeEngineSelect.value = 'jsquash';
        }
    }
    const jsquashSelect = document.getElementById('jsquashResizeMethod');
    if (jsquashSelect) {
        const method = prefs.jsquashResizeMethod || DEFAULT_JSQUASH_METHOD;
        jsquashSelect.value = method;
        if (!jsquashSelect.value) {
            jsquashSelect.value = DEFAULT_JSQUASH_METHOD;
        }
    }
    const photonSelect = document.getElementById('photonResizeFilter');
    if (photonSelect) {
        const filter = Number.isFinite(prefs.photonSamplingFilter)
            ? String(Math.min(5, Math.max(1, prefs.photonSamplingFilter)))
            : String(DEFAULT_PHOTON_SAMPLING_FILTER);
        photonSelect.value = filter;
        if (!photonSelect.value) {
            photonSelect.value = String(DEFAULT_PHOTON_SAMPLING_FILTER);
        }
    }
    if (prefs.aspectRatio && Number.isFinite(prefs.aspectRatio.width) && Number.isFinite(prefs.aspectRatio.height)) {
        currentAspectRatio = prefs.aspectRatio.width / prefs.aspectRatio.height;
        updateActiveAspectButton(null, prefs.aspectRatio.width, prefs.aspectRatio.height);
        const aspectW = document.getElementById('aspectW');
        const aspectH = document.getElementById('aspectH');
        if (aspectW && aspectH) {
            aspectW.value = prefs.aspectRatio.width;
            aspectH.value = prefs.aspectRatio.height;
        }
    } else {
        const width = parseInt(document.getElementById('exportWidth').value);
        const height = parseInt(document.getElementById('exportHeight').value);
        if (width && height) {
            currentAspectRatio = width / height;
            updateAspectInputsFromCurrent();
        }
    }
    if (getResizeMode() === RESIZE_MODES.STATIC_SIZE) {
        const width = parseInt(document.getElementById('exportWidth').value);
        const height = parseInt(document.getElementById('exportHeight').value);
        if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
            currentAspectRatio = width / height;
            updateAspectInputsFromCurrent();
        }
    }

    updateFormatSettings(false);
    syncResizeEngineControls();
    updateResizeModeUI();
    syncAboutCheckbox();
    if (typeof prefs.lightMode === 'boolean') {
        document.documentElement.classList.toggle('light', prefs.lightMode);
        document.body.classList.toggle('light', prefs.lightMode);
        const themeToggle = document.getElementById('themeToggle');
        if (themeToggle) {
            themeToggle.checked = prefs.lightMode;
        }
    }
    requestAnimationFrame(() => {
        document.documentElement.classList.remove('no-theme-transition');
    });
    suppressProfileModified = false;
    updateProfileNameDisplay();
    updateGridInfoLabels();
    updateProfileImportExportButtons();
    updateResizeModeInfoText();
    updateFilenameTipVisibility();
    updateGridSize();
    updatePreviewNudgeControls();
}

function resetPreferences() {
    const existingProfiles = getProfiles();
    localStorage.removeItem(STORAGE_KEY);
    suppressProfileModified = true;

    const defaultWidth = Number.isFinite(profileDefault.exportWidth) ? profileDefault.exportWidth : 1920;
    const defaultHeight = Number.isFinite(profileDefault.exportHeight) ? profileDefault.exportHeight : 1080;
    const defaultAspect = profileDefault.aspectRatio
        && Number.isFinite(profileDefault.aspectRatio.width)
        && Number.isFinite(profileDefault.aspectRatio.height)
        ? profileDefault.aspectRatio
        : { width: defaultWidth, height: defaultHeight };

    document.getElementById('exportWidth').value = defaultWidth;
    document.getElementById('exportHeight').value = defaultHeight;
    const resizeModeSelect = document.getElementById('resizeMode');
    if (resizeModeSelect) {
        resizeModeSelect.value = profileDefault.resizeMode || DEFAULT_RESIZE_MODE;
        if (!resizeModeSelect.value) {
            resizeModeSelect.value = DEFAULT_RESIZE_MODE;
        }
    }
    document.getElementById('exportFormat').value = profileDefault.exportFormat || 'jpeg';
    document.getElementById('jpegQuality').value = Number.isFinite(profileDefault.jpegQuality) ? profileDefault.jpegQuality : 90;
    document.getElementById('webpQuality').value = Number.isFinite(profileDefault.webpQuality) ? profileDefault.webpQuality : 90;
    document.getElementById('avifQuality').value = Number.isFinite(profileDefault.avifQuality) ? profileDefault.avifQuality : 60;
    document.getElementById('jpegQualityNumber').value = Number.isFinite(profileDefault.jpegQuality) ? profileDefault.jpegQuality : 90;
    document.getElementById('webpQualityNumber').value = Number.isFinite(profileDefault.webpQuality) ? profileDefault.webpQuality : 90;
    document.getElementById('avifQualityNumber').value = Number.isFinite(profileDefault.avifQuality) ? profileDefault.avifQuality : 60;
    document.getElementById('pngTransparency').checked = typeof profileDefault.pngTransparency === 'boolean' ? profileDefault.pngTransparency : true;
    document.getElementById('previewDelay').value = '0.5';
    document.getElementById('aboutAutoShow').checked = true;
    document.getElementById('enableSharpening').checked = typeof profileDefault.enableSharpening === 'boolean' ? profileDefault.enableSharpening : false;
    document.getElementById('adaptiveSharpening').checked = typeof profileDefault.adaptiveSharpening === 'boolean' ? profileDefault.adaptiveSharpening : false;
    document.getElementById('sharpenAmount').value = Number.isFinite(profileDefault.sharpenAmount) ? profileDefault.sharpenAmount : 120;
    document.getElementById('sharpenRadius').value = Number.isFinite(profileDefault.sharpenRadius) ? profileDefault.sharpenRadius : 1.0;
    document.getElementById('sharpenThreshold').value = Number.isFinite(profileDefault.sharpenThreshold) ? profileDefault.sharpenThreshold : 10;
    document.getElementById('sharpenAmountNumber').value = Number.isFinite(profileDefault.sharpenAmount) ? profileDefault.sharpenAmount : 120;
    document.getElementById('sharpenRadiusNumber').value = Number.isFinite(profileDefault.sharpenRadius) ? profileDefault.sharpenRadius : 1.0;
    document.getElementById('sharpenThresholdNumber').value = Number.isFinite(profileDefault.sharpenThreshold) ? profileDefault.sharpenThreshold : 10;
    const resizeEngineSelect = document.getElementById('resizeEngine');
    if (resizeEngineSelect) {
        resizeEngineSelect.value = 'jsquash';
    }
    const jsquashSelect = document.getElementById('jsquashResizeMethod');
    if (jsquashSelect) {
        jsquashSelect.value = DEFAULT_JSQUASH_METHOD;
    }
    const photonSelect = document.getElementById('photonResizeFilter');
    if (photonSelect) {
        photonSelect.value = String(DEFAULT_PHOTON_SAMPLING_FILTER);
    }
    const scaleInput = document.getElementById('scalePercent');
    if (scaleInput) {
        const defaultScale = Number.isFinite(profileDefault.scalePercent)
            ? profileDefault.scalePercent
            : DEFAULT_SCALE_PERCENT;
        scaleInput.value = clampScalePercent(defaultScale);
    }
    const aspectW = document.getElementById('aspectW');
    const aspectH = document.getElementById('aspectH');
    if (aspectW && aspectH) {
        aspectW.value = defaultAspect.width;
        aspectH.value = defaultAspect.height;
    }
    const themeToggle = document.getElementById('themeToggle');
    if (themeToggle) {
        themeToggle.checked = false;
    }
    showGridSizeInfo = false;
    showFilenameTip = true;
    gridItemSize = 240;
    updateGridInfoLabels();
    document.documentElement.classList.remove('light');
    document.body.classList.remove('light');

    currentAspectRatio = defaultAspect.width / defaultAspect.height;
    updateActiveAspectButton(null, defaultAspect.width, defaultAspect.height);
    updateFormatSettings(false);
    syncResizeEngineControls();
    updateResizeModeUI();
    persistPreferencesNoDirty({
        aspectRatio: { width: defaultAspect.width, height: defaultAspect.height },
        showGridSizeInfo: false,
        showFilenameTip: true,
        gridItemSize: 240,
        profiles: existingProfiles
    });
    currentProfileName = '';
    profileModified = false;
    lastLoadedProfile = null;
    lastExportPreviewMode = 'none';
    suppressProfileModified = false;
    updateProfileNameDisplay();
}

function updateActiveAspectButton(buttonEl, width, height) {
    const select = document.getElementById('aspectPresetSelect');
    if (!select) return;
    const match = Array.from(select.options).find(option => {
        const w = parseInt(option.dataset.aspectW);
        const h = parseInt(option.dataset.aspectH);
        return w === width && h === height;
    });
    select.value = match ? match.value : '';
}

function syncAboutCheckbox() {
    const prefs = getPreferences();
    const checkbox = document.getElementById('aboutAutoShow');
    if (typeof prefs.showAboutOnLoad === 'boolean') {
        checkbox.checked = prefs.showAboutOnLoad;
    } else {
        checkbox.checked = true;
    }
}

function shouldShowAboutOnLoad() {
    const prefs = getPreferences();
    if (typeof prefs.showAboutOnLoad === 'boolean') {
        return prefs.showAboutOnLoad;
    }
    return true;
}

Object.assign(window, {
    openAbout,
    openFiles,
    openFilesFromHeader,
    exportImages,
    cancelExport,
    clearAllImages,
    toggleDrawer,
    setAspectRatio,
    updateFormatSettings,
    adjustZoom,
    resetZoom,
    setScalePercentPreset,
    toggleTheme,
    toggleGridInfo,
    alignAllCrops,
    toggleSplitPreview,
    setPreviewLayout,
    togglePreviewFullscreen,
    closePreview,
    testExport,
    toggleProfiles,
    saveProfile,
    clearProfileName,
    revertProfileSettings,
    resetProfileToDefault,
    resetPreferences,
    exportUserProfiles,
    importUserProfiles,
    closeAbout,
    openChangelog,
    closeChangelog,
    closeMobileWarning,
    closeExportPreview
});

renderAspectPresetButtons();
applyPreferences();
updatePreviewLayoutButtons();
updatePreviewFullscreenButton();
updateTestExportButton();
updateMainResizerState();
if (shouldShowAboutOnLoad()) {
    openAbout();
}
updateProfileNameDisplay();
suppressDirty = false;
clearDirtyState();
document.body.classList.remove('app-initializing');
